import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { StatCard } from "../components/StatCard";
import { InventoryTable } from "../components/InventoryTable";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { ColumnPicker, ColumnDefinition } from "../components/ui/column-picker";
import { QuickFilters } from "../components/ui/quick-filters";
import type { FilterOptions } from "../components/ui/quick-filters";
import { Package, TrendingUp, DollarSign, Activity, Plus, Minus, Search, Settings2, Filter, Download, Check, AlertCircle, X, Trash2 } from "lucide-react";
import { Transaction } from "../types";
import { DataService } from "../services/supabase";
import { CalculationService } from "../services/calculationService";
import { exportToSpreadsheet, formatExportValue } from "../services/exportService";
import { UserPreferencesService } from "../services/userPreferencesService";
import { PreferencesService } from "../services/preferencesService";
import { uploadCardImage, deleteCardImages } from "../services/cardImageService";
import { QuantitySelectionDialog } from "../components/QuantitySelectionDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

interface IndexProps {
  user?: any;
  onSignOut?: () => void;
  dataService?: DataService | null;
  onTransactionsUpdate?: (transactions: Transaction[]) => void;
  onGradingOrdersUpdate?: (gradingOrders: any[]) => void;
  transactions?: Transaction[];
  gradingOrders?: any[];
  expenses?: any[];
}

type CardCatalogFieldKey =
  | "card_number"
  | "subject"
  | "league"
  | "insert_name"
  | "parallel"
  | "numbering";

const emptyCardDetailFields: Record<CardCatalogFieldKey, string> = {
  card_number: "",
  subject: "",
  league: "",
  insert_name: "",
  parallel: "",
  numbering: "",
};

function CardCatalogFields({
  idPrefix,
  values,
  onFieldChange,
}: {
  idPrefix: string;
  values: Record<CardCatalogFieldKey, string>;
  onFieldChange: (key: CardCatalogFieldKey, value: string) => void;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-card_number`}>Card #</Label>
        <Input
          id={`${idPrefix}-card_number`}
          value={values.card_number}
          onChange={(e) => onFieldChange("card_number", e.target.value)}
          placeholder="e.g., 23, RC-1"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-subject`}>Player / Character</Label>
        <Input
          id={`${idPrefix}-subject`}
          value={values.subject}
          onChange={(e) => onFieldChange("subject", e.target.value)}
          placeholder="e.g., Shohei Ohtani, Charizard"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-league`}>League</Label>
        <Input
          id={`${idPrefix}-league`}
          value={values.league}
          onChange={(e) => onFieldChange("league", e.target.value)}
          placeholder="e.g., NBA, NCAA, Premier League"
        />
        <p className="text-xs text-muted-foreground">Separate from Sport/TCG when you need both.</p>
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-insert_name`}>Insert / Subset</Label>
        <Input
          id={`${idPrefix}-insert_name`}
          value={values.insert_name}
          onChange={(e) => onFieldChange("insert_name", e.target.value)}
          placeholder="e.g., Image Variation, Silver Prizm"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-parallel`}>Parallel</Label>
        <Input
          id={`${idPrefix}-parallel`}
          value={values.parallel}
          onChange={(e) => onFieldChange("parallel", e.target.value)}
          placeholder="e.g., Gold /50, Blue Wave"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-numbering`}>Print run / Numbering</Label>
        <Input
          id={`${idPrefix}-numbering`}
          value={values.numbering}
          onChange={(e) => onFieldChange("numbering", e.target.value)}
          placeholder="e.g., 12/99, 1st Edition"
        />
      </div>
    </div>
  );
}

// Column definitions for inventory and sold tables
const inventoryColumns: ColumnDefinition[] = [
  { key: 'card', label: 'Card', type: 'text', sortable: true, filterable: true, required: true, width: '220px' },
  { key: 'sport', label: 'Sport/TCG', type: 'chip', sortable: true, filterable: true, width: '110px' },
  { key: 'purchase_date', label: 'Purchase Date', type: 'date', sortable: true, filterable: true, width: '130px' },
  { key: 'purchase_platform', label: 'Platform', type: 'chip', sortable: true, filterable: true, width: '140px' },
  { key: 'cost', label: 'Price', type: 'number', sortable: true, filterable: true, width: '100px' },
  { key: 'totalCost', label: 'Total Cost', type: 'number', sortable: true, filterable: true, width: '100px' },
  { key: 'daysInInventory', label: 'Days in Inventory', type: 'number', sortable: true, filterable: true, width: '100px' },
  { key: 'grade', label: 'Grade', type: 'chip', sortable: true, filterable: true, width: '100px' },
  { key: 'year', label: 'Year', type: 'chip', sortable: true, filterable: true, width: '80px' },
  { key: 'manufacturer', label: 'Manufacturer', type: 'chip', sortable: true, filterable: true, width: '130px' },
  { key: 'set', label: 'Set', type: 'chip', sortable: true, filterable: true, width: '150px' },
  { key: 'card_number', label: 'Card #', type: 'chip', sortable: false, filterable: true, width: '90px' },
  { key: 'subject', label: 'Player / Character', type: 'chip', sortable: false, filterable: true, width: '140px' },
  { key: 'league', label: 'League', type: 'chip', sortable: false, filterable: true, width: '100px' },
  { key: 'insert_name', label: 'Insert / Subset', type: 'chip', sortable: false, filterable: true, width: '140px' },
  { key: 'parallel', label: 'Parallel', type: 'chip', sortable: false, filterable: true, width: '120px' },
  { key: 'numbering', label: 'Print Run / Numbering', type: 'chip', sortable: false, filterable: true, width: '130px' },
  { key: 'bought_from', label: 'Bought From', type: 'text', sortable: true, filterable: true, width: '120px' },
  { key: 'other', label: 'Other Info', type: 'text', sortable: true, filterable: true, width: '150px' },
  { key: 'notes', label: 'Notes', type: 'text', sortable: true, filterable: true, width: '150px' },
];

const soldColumns: ColumnDefinition[] = [
  { key: 'card', label: 'Card', type: 'text', sortable: true, filterable: true, required: true, width: '220px' },
  { key: 'sport', label: 'Sport/TCG', type: 'chip', sortable: true, filterable: true, width: '110px' },
  { key: 'purchase_date', label: 'Purchase Date', type: 'date', sortable: true, filterable: true, width: '130px' },
  { key: 'purchase_platform', label: 'Purchase Platform', type: 'chip', sortable: true, filterable: true, width: '140px' },
  { key: 'cost', label: 'Purchase Price', type: 'number', sortable: true, filterable: true, width: '120px' },
  { key: 'totalCost', label: 'Total Cost', type: 'number', sortable: true, filterable: true, width: '100px' },
  { key: 'daysInInventory', label: 'Days in Inventory', type: 'number', sortable: true, filterable: true, width: '100px' },
  { key: 'grade', label: 'Grade', type: 'chip', sortable: true, filterable: true, width: '100px' },
  { key: 'year', label: 'Year', type: 'chip', sortable: true, filterable: true, width: '80px' },
  { key: 'manufacturer', label: 'Manufacturer', type: 'chip', sortable: true, filterable: true, width: '130px' },
  { key: 'set', label: 'Set', type: 'chip', sortable: true, filterable: true, width: '150px' },
  { key: 'card_number', label: 'Card #', type: 'chip', sortable: false, filterable: true, width: '90px' },
  { key: 'subject', label: 'Player / Character', type: 'chip', sortable: false, filterable: true, width: '140px' },
  { key: 'league', label: 'League', type: 'chip', sortable: false, filterable: true, width: '100px' },
  { key: 'insert_name', label: 'Insert / Subset', type: 'chip', sortable: false, filterable: true, width: '140px' },
  { key: 'parallel', label: 'Parallel', type: 'chip', sortable: false, filterable: true, width: '120px' },
  { key: 'numbering', label: 'Print Run / Numbering', type: 'chip', sortable: false, filterable: true, width: '130px' },
  { key: 'bought_from', label: 'Bought From', type: 'text', sortable: true, filterable: true, width: '120px' },
  { key: 'other', label: 'Other Info', type: 'text', sortable: true, filterable: true, width: '150px' },
  { key: 'sale_date', label: 'Sale Date', type: 'date', sortable: true, filterable: true, width: '130px' },
  { key: 'sale_price', label: 'Sale Price', type: 'number', sortable: true, filterable: true, width: '100px' },
  { key: 'sale_platform', label: 'Sale Platform', type: 'chip', sortable: true, filterable: true, width: '140px' },
  { key: 'sold_to', label: 'Sold To', type: 'text', sortable: true, filterable: true, width: '120px' },
  { key: 'marketplace_fees', label: 'Marketplace Fees', type: 'number', sortable: true, filterable: true, width: '120px' },
  { key: 'shipping_fees', label: 'Shipping Fees', type: 'number', sortable: true, filterable: true, width: '100px' },
  { key: 'netProfit', label: 'Net Profit', type: 'number', sortable: true, filterable: true, width: '100px' },
  { key: 'notes', label: 'Notes', type: 'text', sortable: true, filterable: true, width: '150px' },
];

const Index = ({ user, onSignOut, dataService, onTransactionsUpdate, onGradingOrdersUpdate, transactions = [], gradingOrders = [], expenses = [] }: IndexProps) => {
  // State for search and filtering
  const [searchTerm, setSearchTerm] = useState('');
  
  // State for sorting
  const [sortField, setSortField] = useState<'purchase_date' | 'card' | 'sport' | 'cost' | 'grade'>('purchase_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // State for column visibility
  const [inventoryVisibleColumns, setInventoryVisibleColumns] = useState<string[]>([
    'card', 'sport', 'purchase_date', 'purchase_platform', 'cost', 'totalCost', 
    'daysInInventory', 'grade', 'year', 'manufacturer', 'set', 'subject', 'parallel'
  ]);
  const [soldVisibleColumns, setSoldVisibleColumns] = useState<string[]>([
    'card', 'sport', 'purchase_date', 'purchase_platform', 'cost', 'grade', 
    'year', 'manufacturer', 'set', 'subject', 'parallel', 'sale_date', 'sale_price', 'sale_platform', 'netProfit'
  ]);
  const [inventoryColumnOrder, setInventoryColumnOrder] = useState<string[]>(
    inventoryColumns.map(col => col.key)
  );
  const [soldColumnOrder, setSoldColumnOrder] = useState<string[]>(
    soldColumns.map(col => col.key)
  );
  const [columnPickerOpen, setColumnPickerOpen] = useState(false);
  const [activeColumnPicker, setActiveColumnPicker] = useState<'inventory' | 'sold'>('inventory');
  
  // State for quick filters
  const [quickFiltersOpen, setQuickFiltersOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState<FilterOptions>({});
  
  // Load column preferences from database on mount
  useEffect(() => {
    const loadPreferences = async () => {
      if (!user?.email) return;
      
      try {
        const preferences = await UserPreferencesService.getColumnPreferences(user.email);
        if (preferences) {
          if (preferences.inventory) {
            if (preferences.inventory.visibleColumns) {
              setInventoryVisibleColumns(preferences.inventory.visibleColumns);
            }
            if (preferences.inventory.columnOrder) {
              setInventoryColumnOrder(preferences.inventory.columnOrder);
            }
          }
          if (preferences.sold) {
            if (preferences.sold.visibleColumns) {
              setSoldVisibleColumns(preferences.sold.visibleColumns);
            }
            if (preferences.sold.columnOrder) {
              setSoldColumnOrder(preferences.sold.columnOrder);
            }
          }
        }
      } catch (error) {
        console.warn('Failed to load column preferences:', error);
      }
    };
    
    loadPreferences();
  }, [user?.email]);
  
  // Save column preferences to database whenever they change
  useEffect(() => {
    const savePreferences = async () => {
      if (!user?.email) return;
      
      // Skip initial save (wait for at least one change)
      if (
        inventoryVisibleColumns.length === 0 && 
        soldVisibleColumns.length === 0
      ) {
        return;
      }
      
      try {
        const preferences = {
          inventory: {
            visibleColumns: inventoryVisibleColumns,
            columnOrder: inventoryColumnOrder
          },
          sold: {
            visibleColumns: soldVisibleColumns,
            columnOrder: soldColumnOrder
          }
        };
        
        await UserPreferencesService.saveColumnPreferences(user.email, preferences);
      } catch (error) {
        console.warn('Failed to save column preferences:', error);
      }
    };
    
    // Debounce saves to avoid too many database writes
    const timeoutId = setTimeout(savePreferences, 500);
    return () => clearTimeout(timeoutId);
  }, [user?.email, inventoryVisibleColumns, inventoryColumnOrder, soldVisibleColumns, soldColumnOrder]);
  
  // State for exports
  const [isExporting, setIsExporting] = useState(false);
  
  // State for Add Card dialog
  const [addCardDialogOpen, setAddCardDialogOpen] = useState(false);
  const [isLotMode, setIsLotMode] = useState(false);
  const [lotCards, setLotCards] = useState<any[]>([]);
  const [lotOrderData, setLotOrderData] = useState({
    purchaseDate: '',
    purchasePlatform: '',
    boughtFrom: '',
  });
  const [lotConfirmationDialogOpen, setLotConfirmationDialogOpen] = useState(false);
  const [newCardData, setNewCardData] = useState({
    card: '',
    sport: '',
    purchaseDate: '',
    purchasePlatform: '',
    purchasePrice: '',
    year: '',
    manufacturer: '',
    set: '',
    ...emptyCardDetailFields,
    other: '',
    grade: '',
    gradingOrderId: '',
    boughtFrom: '',
    notes: '',
    numberOfCards: 1,
    frontImageFile: null as File | null,
    backImageFile: null as File | null,
    otherImageFiles: [] as File[],
  });
  
  const MAX_OTHER_IMAGES = 3;
  const [cardImagePreviewBehavior, setCardImagePreviewBehavior] = useState<'hover' | 'click'>(
    PreferencesService.getPreferences().cardImagePreviewBehavior || 'hover'
  );
  const [cardImagePreviewSize, setCardImagePreviewSize] = useState<'small' | 'medium' | 'large'>(
    PreferencesService.getPreferences().cardImagePreviewSize || 'small'
  );

  useEffect(() => {
    const preferences = PreferencesService.getPreferences();
    setCardImagePreviewBehavior(preferences.cardImagePreviewBehavior || 'hover');
    setCardImagePreviewSize(preferences.cardImagePreviewSize || 'small');
  }, []);
  
  // State for Move to Grading dialog
  const [gradingDialogOpen, setGradingDialogOpen] = useState(false);
  const [gradingTransaction, setGradingTransaction] = useState<Transaction | null>(null);
  const [gradingDialogStep, setGradingDialogStep] = useState<'selectOrder' | 'newOrder'>('selectOrder');
  const [selectedGradingOrderId, setSelectedGradingOrderId] = useState<string>('');
  
  // State for creating new grading order from inventory
  const [newOrderFromInventoryData, setNewOrderFromInventoryData] = useState({
    orderId: '',
    company: '',
    serviceLevel: '',
    submissionDate: '',
    cost: '',
    notes: '',
  });
  
  // State for Mark as Sold dialog
  const [soldDialogOpen, setSoldDialogOpen] = useState(false);
  const [soldTransaction, setSoldTransaction] = useState<Transaction | null>(null);
  const [soldData, setSoldData] = useState({
    saleDate: '',
    salePrice: '',
    salePlatform: '',
    marketplaceFees: '',
    shippingFees: '',
    soldTo: '',
    // For multiple cards from grouped transaction
    individualSalePrices: {} as Record<string, string>,
    individualMarketplaceFees: {} as Record<string, string>,
    individualShippingFees: {} as Record<string, string>,
  });

  // State for quantity selection dialog (for grouped transactions)
  const [quantityDialogOpen, setQuantityDialogOpen] = useState(false);
  const [pendingTransaction, setPendingTransaction] = useState<Transaction | null>(null);
  const [pendingQuantity, setPendingQuantity] = useState<number>(0);
  const [pendingAction, setPendingAction] = useState<'sell' | 'grading'>('sell');
  // Use ref to track confirmation synchronously (state updates are async)
  const isConfirmingQuantityRef = React.useRef(false);
  
  // State for bulk quantity selection (for sold)
  const [bulkQuantitySelections, setBulkQuantitySelections] = useState<Map<string, number>>(new Map());
  const [bulkQuantityDialogOpen, setBulkQuantityDialogOpen] = useState(false);
  const [currentBulkQuantityTransaction, setCurrentBulkQuantityTransaction] = useState<Transaction | null>(null);
  const [bulkQuantityTransactionsQueue, setBulkQuantityTransactionsQueue] = useState<Transaction[]>([]);
  
  // State for bulk grading quantity selection
  const [bulkGradingQuantitySelections, setBulkGradingQuantitySelections] = useState<Map<string, number>>(new Map());
  const [bulkGradingQuantityDialogOpen, setBulkGradingQuantityDialogOpen] = useState(false);
  const [currentBulkGradingQuantityTransaction, setCurrentBulkGradingQuantityTransaction] = useState<Transaction | null>(null);
  const [bulkGradingQuantityTransactionsQueue, setBulkGradingQuantityTransactionsQueue] = useState<Transaction[]>([]);
  const [bulkGradingTotalCardCount, setBulkGradingTotalCardCount] = useState<number>(0);
  const [storedBulkGradingQuantitySelections, setStoredBulkGradingQuantitySelections] = useState<Map<string, number>>(new Map());
  
  // State for bulk selection
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // State for bulk actions
  const [bulkGradingDialogOpen, setBulkGradingDialogOpen] = useState(false);
  const [bulkSoldDialogOpen, setBulkSoldDialogOpen] = useState(false);
  const [bulkGradingDialogStep, setBulkGradingDialogStep] = useState<'selectOrder' | 'newOrder'>('selectOrder');
  const [bulkSelectedGradingOrderId, setBulkSelectedGradingOrderId] = useState<string>('');

  // State for delete card confirmation dialog
  const [cardToDeleteId, setCardToDeleteId] = useState<string | null>(null);
  // State for bulk delete confirmation dialog
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Inline success/error message (replaces browser alerts)
  const [indexMessage, setIndexMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const showIndexMessage = useCallback((type: "success" | "error", text: string) => {
    setIndexMessage({ type, text });
    setTimeout(() => setIndexMessage(null), 4000);
  }, []);
  
  // State for creating new grading order from bulk action
  const [newOrderFromBulkData, setNewOrderFromBulkData] = useState({
    orderId: '',
    company: '',
    serviceLevel: '',
    submissionDate: '',
    cost: '',
    notes: '',
  });
  const [bulkSoldData, setBulkSoldData] = useState<{
    saleDate: string;
    salePlatform: string;
    soldTo: string;
    splitPriceEqually: boolean;
    totalSalePrice: string;
    individualSalePrices: Record<string, string>;
    splitFeesEqually: boolean;
    totalMarketplaceFees: string;
    totalShippingFees: string;
    individualMarketplaceFees: Record<string, string>;
    individualShippingFees: Record<string, string>;
    _totalCardCount?: number;
    _transactionIdMap?: Map<string, Transaction>;
    _quantitySelections?: Map<string, number>;
    _isSingleTransaction?: boolean;
    _singleTransaction?: Transaction;
  }>({
    saleDate: '',
    salePlatform: '',
    soldTo: '',
    splitPriceEqually: false,
    totalSalePrice: '',
    individualSalePrices: {},
    splitFeesEqually: false,
    totalMarketplaceFees: '',
    totalShippingFees: '',
    individualMarketplaceFees: {},
    individualShippingFees: {},
  });
  
  // Get in-progress grading orders for dropdown
  const inProgressGradingOrders = useMemo(() => {
    const enrichedOrders = CalculationService.enrichGradingOrders(gradingOrders, transactions);
    return enrichedOrders.filter(order => !order.return_date);
  }, [gradingOrders, transactions]);

  // Quick lookup for grading orders by order_id
  const gradingOrdersByOrderId = useMemo(() => {
    const map = new Map<string, any>();
    gradingOrders.forEach(order => {
      if (order?.order_id) {
        map.set(order.order_id.trim(), order);
      }
    });
    return map;
  }, [gradingOrders]);

  const isTransactionInActiveGradingOrder = useCallback((transaction: Transaction) => {
    const gradingId = transaction.grading_order_id?.toString().trim();
    if (!gradingId) {
      return false;
    }

    const linkedOrder = gradingOrdersByOrderId.get(gradingId);
    if (!linkedOrder) {
      return true;
    }

    const hasReturnDate =
      typeof linkedOrder.return_date === 'string'
        ? linkedOrder.return_date.trim() !== ''
        : Boolean(linkedOrder.return_date);

    return !hasReturnDate;
  }, [gradingOrdersByOrderId]);

  const isTransactionEligibleForGrading = useCallback((transaction: Transaction) => {
    const saleDate = transaction.sale_date;
    const notSold =
      !saleDate ||
      (typeof saleDate === 'string' && saleDate.trim() === '');

    if (!notSold) {
      return false;
    }

    const hasGrade =
      typeof transaction.grade === 'string'
        ? transaction.grade.trim() !== ''
        : Boolean(transaction.grade);

    if (hasGrade) {
      return false;
    }

    return !isTransactionInActiveGradingOrder(transaction);
  }, [isTransactionInActiveGradingOrder]);
  
  // Helper function to create a unique key for grouping identical transactions
  const getTransactionGroupKey = (transaction: Transaction): string => {
    // Create a key based on ALL fields that should match for grouping
    // Exclude id, created_at, updated_at, and calculated fields
    const keyParts = [
      transaction.card || '',
      transaction.sport || '',
      transaction.purchase_date || '',
      transaction.purchase_platform || '',
      String(transaction.cost || '0'),
      transaction.grading_order_id || '',
      transaction.grade || '',
      String(transaction.year || ''),
      transaction.manufacturer || '',
      transaction.set || '',
      transaction.card_number || '',
      transaction.subject || '',
      transaction.league || '',
      transaction.insert_name || '',
      transaction.parallel || '',
      transaction.numbering || '',
      transaction.bought_from || '',
      transaction.other || '',
      transaction.notes || '',
      transaction.front_image_path || '',
      transaction.back_image_path || '',
      Array.isArray(transaction.other_image_paths) ? transaction.other_image_paths.join(',') : '',
    ];
    return keyParts.join('|');
  };

  // Type for grouped transactions
  type GroupedTransaction = Transaction & { _count?: number; _originalIds?: string[] };

  // Helper function to group identical transactions
  const groupIdenticalTransactions = (transactions: Transaction[]): GroupedTransaction[] => {
    const groups = new Map<string, Transaction[]>();
    
    // Group transactions by their unique key
    transactions.forEach(transaction => {
      const key = getTransactionGroupKey(transaction);
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(transaction);
    });
    
    // Create grouped transactions with count
    const grouped: GroupedTransaction[] = [];
    groups.forEach((groupTransactions, key) => {
      if (groupTransactions.length > 1) {
        // Multiple identical transactions - create grouped entry
        const representative: GroupedTransaction = Object.assign(
          {},
          groupTransactions[0],
          {
            _count: groupTransactions.length,
            _originalIds: groupTransactions.map(t => t.id).filter(Boolean) as string[]
          }
        );
        grouped.push(representative);
      } else {
        // Single transaction - add as is
        grouped.push(groupTransactions[0]);
      }
    });
    
    return grouped;
  };

  // Process transactions into inventory items (non-sold, non-grading) and sold items
  const baseInventoryItems = useMemo(() => {
    return transactions.filter(t => {
      const saleDate = t.sale_date;
      const notSold =
        !saleDate ||
        (typeof saleDate === 'string' && saleDate.trim() === '');

      if (!notSold) {
        return false;
      }

      return !isTransactionInActiveGradingOrder(t);
    });
  }, [transactions, isTransactionInActiveGradingOrder]);
  
  const baseSoldItems = useMemo(() => {
    return transactions.filter(t => {
      // Items are sold if they have a sale_date and it's not empty
      return t.sale_date && t.sale_date.trim() !== '';
    });
  }, [transactions]);
  
  // Apply search filter and sorting
  const inventoryItems = useMemo(() => {
    let filtered = baseInventoryItems;
    
    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = baseInventoryItems.filter(t => {
        const card = t.card?.toLowerCase() || '';
        const sport = t.sport?.toLowerCase() || '';
        const platform = t.purchase_platform?.toLowerCase() || '';
        const grade = t.grade?.toLowerCase() || '';
        const manufacturer = t.manufacturer?.toLowerCase() || '';
        const set = t.set?.toLowerCase() || '';
        const subject = t.subject?.toLowerCase() || '';
        const league = t.league?.toLowerCase() || '';
        const insertName = t.insert_name?.toLowerCase() || '';
        const parallel = t.parallel?.toLowerCase() || '';
        const numbering = t.numbering?.toLowerCase() || '';
        const cardNumber = t.card_number?.toLowerCase() || '';
        
        return card.includes(searchLower) || 
               sport.includes(searchLower) || 
               platform.includes(searchLower) || 
               grade.includes(searchLower) ||
               manufacturer.includes(searchLower) ||
               set.includes(searchLower) ||
               subject.includes(searchLower) ||
               league.includes(searchLower) ||
               insertName.includes(searchLower) ||
               parallel.includes(searchLower) ||
               numbering.includes(searchLower) ||
               cardNumber.includes(searchLower);
      });
    }
    
    // Apply quick filters
    if (Object.keys(activeFilters).length > 0) {
      filtered = filtered.filter(t => {
        // Sport filter (array)
        if (activeFilters.sport && activeFilters.sport.length > 0) {
          if (!activeFilters.sport.includes(t.sport || '')) return false;
        }
        
        // Platform filter (array)
        if (activeFilters.platform && activeFilters.platform.length > 0) {
          if (!activeFilters.platform.includes(t.purchase_platform || '')) return false;
        }
        
        // Grade filter (array)
        if (activeFilters.grade && activeFilters.grade.length > 0) {
          if (!activeFilters.grade.includes(t.grade || '')) return false;
        }

        if (activeFilters.subject && activeFilters.subject.length > 0) {
          if (!activeFilters.subject.includes(t.subject || '')) return false;
        }

        if (activeFilters.parallel && activeFilters.parallel.length > 0) {
          if (!activeFilters.parallel.includes(t.parallel || '')) return false;
        }
        
        // Year filter (array)
        if (activeFilters.year && activeFilters.year.length > 0) {
          if (!activeFilters.year.includes(String(t.year || ''))) return false;
        }
        
        // Price range filters
        const cost = typeof t.cost === 'number' ? t.cost : parseFloat(t.cost || '0');
        if (activeFilters.minPrice !== undefined && cost < activeFilters.minPrice) return false;
        if (activeFilters.maxPrice !== undefined && cost > activeFilters.maxPrice) return false;
        
        // Date range filters
        if (activeFilters.startDate || activeFilters.endDate) {
          const purchaseDate = new Date(t.purchase_date || '');
          if (activeFilters.startDate && purchaseDate < new Date(activeFilters.startDate)) return false;
          if (activeFilters.endDate && purchaseDate > new Date(activeFilters.endDate)) return false;
        }
        
        return true;
      });
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: any;
      let bValue: any;
      
      switch (sortField) {
        case 'purchase_date':
          aValue = new Date(a.purchase_date || '').getTime();
          bValue = new Date(b.purchase_date || '').getTime();
          break;
        case 'card':
          aValue = a.card?.toLowerCase() || '';
          bValue = b.card?.toLowerCase() || '';
          break;
        case 'sport':
          aValue = a.sport?.toLowerCase() || '';
          bValue = b.sport?.toLowerCase() || '';
          break;
        case 'cost':
          aValue = typeof a.cost === 'number' ? a.cost : parseFloat(a.cost || '0');
          bValue = typeof b.cost === 'number' ? b.cost : parseFloat(b.cost || '0');
          break;
        case 'grade':
          aValue = a.grade?.toLowerCase() || '';
          bValue = b.grade?.toLowerCase() || '';
          break;
        default:
          return 0;
      }
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      return 0;
    });
    
    // Group identical transactions after filtering and sorting
    return groupIdenticalTransactions(filtered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseInventoryItems, searchTerm, sortField, sortDirection, activeFilters]);
  
  const soldItems = useMemo(() => {
    let filtered = baseSoldItems;
    
    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = baseSoldItems.filter(t => {
        const card = t.card?.toLowerCase() || '';
        const sport = t.sport?.toLowerCase() || '';
        const platform = t.purchase_platform?.toLowerCase() || '';
        const grade = t.grade?.toLowerCase() || '';
        const manufacturer = t.manufacturer?.toLowerCase() || '';
        const set = t.set?.toLowerCase() || '';
        const subject = t.subject?.toLowerCase() || '';
        const league = t.league?.toLowerCase() || '';
        const insertName = t.insert_name?.toLowerCase() || '';
        const parallel = t.parallel?.toLowerCase() || '';
        const numbering = t.numbering?.toLowerCase() || '';
        const cardNumber = t.card_number?.toLowerCase() || '';
        
        return card.includes(searchLower) || 
               sport.includes(searchLower) || 
               platform.includes(searchLower) || 
               grade.includes(searchLower) ||
               manufacturer.includes(searchLower) ||
               set.includes(searchLower) ||
               subject.includes(searchLower) ||
               league.includes(searchLower) ||
               insertName.includes(searchLower) ||
               parallel.includes(searchLower) ||
               numbering.includes(searchLower) ||
               cardNumber.includes(searchLower);
      });
    }
    
    // Apply quick filters
    if (Object.keys(activeFilters).length > 0) {
      filtered = filtered.filter(t => {
        // Sport filter (array)
        if (activeFilters.sport && activeFilters.sport.length > 0) {
          if (!activeFilters.sport.includes(t.sport || '')) return false;
        }
        
        // Platform filter (array)
        if (activeFilters.platform && activeFilters.platform.length > 0) {
          if (!activeFilters.platform.includes(t.purchase_platform || '')) return false;
        }
        
        // Grade filter (array)
        if (activeFilters.grade && activeFilters.grade.length > 0) {
          if (!activeFilters.grade.includes(t.grade || '')) return false;
        }

        if (activeFilters.subject && activeFilters.subject.length > 0) {
          if (!activeFilters.subject.includes(t.subject || '')) return false;
        }

        if (activeFilters.parallel && activeFilters.parallel.length > 0) {
          if (!activeFilters.parallel.includes(t.parallel || '')) return false;
        }
        
        // Year filter (array)
        if (activeFilters.year && activeFilters.year.length > 0) {
          if (!activeFilters.year.includes(String(t.year || ''))) return false;
        }
        
        // Price range filters
        const cost = typeof t.cost === 'number' ? t.cost : parseFloat(t.cost || '0');
        if (activeFilters.minPrice !== undefined && cost < activeFilters.minPrice) return false;
        if (activeFilters.maxPrice !== undefined && cost > activeFilters.maxPrice) return false;
        
        // Date range filters
        if (activeFilters.startDate || activeFilters.endDate) {
          const purchaseDate = new Date(t.purchase_date || '');
          if (activeFilters.startDate && purchaseDate < new Date(activeFilters.startDate)) return false;
          if (activeFilters.endDate && purchaseDate > new Date(activeFilters.endDate)) return false;
        }
        
        return true;
      });
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: any;
      let bValue: any;
      
      switch (sortField) {
        case 'purchase_date':
          aValue = new Date(a.purchase_date || '').getTime();
          bValue = new Date(b.purchase_date || '').getTime();
          break;
        case 'card':
          aValue = a.card?.toLowerCase() || '';
          bValue = b.card?.toLowerCase() || '';
          break;
        case 'sport':
          aValue = a.sport?.toLowerCase() || '';
          bValue = b.sport?.toLowerCase() || '';
          break;
        case 'cost':
          aValue = typeof a.cost === 'number' ? a.cost : parseFloat(a.cost || '0');
          bValue = typeof b.cost === 'number' ? b.cost : parseFloat(b.cost || '0');
          break;
        case 'grade':
          aValue = a.grade?.toLowerCase() || '';
          bValue = b.grade?.toLowerCase() || '';
          break;
        default:
          return 0;
      }
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      return 0;
    });
    
    return filtered;
  }, [baseSoldItems, searchTerm, sortField, sortDirection, activeFilters]);

  const selectedTransactions = useMemo<Transaction[]>(() => {
    if (selectedItems.size === 0) {
      return [];
    }
    return inventoryItems.filter(
      (t) => t.id && selectedItems.has(t.id)
    );
  }, [inventoryItems, selectedItems]);

  const hasIneligibleSelection = useMemo(() => {
    if (selectedTransactions.length === 0) {
      return false;
    }
    return selectedTransactions.some(
      (transaction) => !isTransactionEligibleForGrading(transaction)
    );
  }, [selectedTransactions, isTransactionEligibleForGrading]);
  
  // Calculate real statistics from data
  const totalInventoryValue = inventoryItems.reduce((sum, t: any) => {
    const cost = typeof t.cost === 'number' ? t.cost : parseFloat(t.cost || '0');
    const count = t._count || 1;
    return sum + (cost * count);
  }, 0);
  
  const totalSales = soldItems.length;
  
  const totalProfit = soldItems.reduce((sum, t) => {
    const salePrice = typeof t.sale_price === 'number' ? t.sale_price : parseFloat(t.sale_price || '0');
    const cost = typeof t.cost === 'number' ? t.cost : parseFloat(t.cost || '0');
    return sum + (salePrice - cost);
  }, 0);
  
  const itemsInInventory = inventoryItems.reduce((sum, t: any) => {
    return sum + (t._count || 1);
  }, 0);
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Enrich inventory items with calculated fields for export
  const enrichedInventoryItems = useMemo(() => {
    return CalculationService.enrichTransactions(inventoryItems, gradingOrders);
  }, [inventoryItems, gradingOrders]);

  // Enrich sold items with calculated fields for export
  const enrichedSoldItems = useMemo(() => {
    return CalculationService.enrichTransactions(soldItems, gradingOrders);
  }, [soldItems, gradingOrders]);

  // Helper to map column keys to display labels
  const getColumnLabel = (key: string, isSold: boolean = false): string => {
    const columns = isSold ? soldColumns : inventoryColumns;
    const column = columns.find(col => col.key === key);
    return column?.label || key;
  };

  // Export inventory data
  const handleExportInventory = () => {
    if (isExporting) return;
    
    setIsExporting(true);
    try {
      // Filter to only visible columns and format data
      const exportData = enrichedInventoryItems.map(item => {
        const row: any = {};
        inventoryVisibleColumns.forEach(colKey => {
          const label = getColumnLabel(colKey, false);
          let value = (item as any)[colKey];
          
          // Handle special fields
          if (colKey === 'totalCost') {
            value = (item as any).calculated_total_cost || value;
          } else if (colKey === 'daysInInventory') {
            // Calculate days in inventory if not already present
            if (item.purchase_date) {
              const purchaseDate = new Date(item.purchase_date);
              const today = new Date();
              const diffTime = Math.abs(today.getTime() - purchaseDate.getTime());
              value = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            }
          }
          
          row[label] = formatExportValue(value);
        });
        return row;
      });

      exportToSpreadsheet(exportData, {
        filename: 'inventory',
        includeDateInFilename: true,
      });
    } catch (error) {
      console.error('Error exporting inventory:', error);
      showIndexMessage('error', 'Failed to export inventory. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Export sold items data
  const handleExportSold = () => {
    if (isExporting) return;
    
    setIsExporting(true);
    try {
      // Filter to only visible columns and format data
      const exportData = enrichedSoldItems.map(item => {
        const row: any = {};
        soldVisibleColumns.forEach(colKey => {
          const label = getColumnLabel(colKey, true);
          let value = (item as any)[colKey];
          
          // Handle special fields
          if (colKey === 'totalCost') {
            value = (item as any).calculated_total_cost || value;
          } else if (colKey === 'daysInInventory') {
            // Calculate days in inventory if not already present
            if (item.purchase_date && item.sale_date) {
              const purchaseDate = new Date(item.purchase_date);
              const saleDate = new Date(item.sale_date);
              const diffTime = Math.abs(saleDate.getTime() - purchaseDate.getTime());
              value = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            }
          } else if (colKey === 'netProfit') {
            value = (item as any).calculated_net_profit || value;
          }
          
          row[label] = formatExportValue(value);
        });
        return row;
      });

      exportToSpreadsheet(exportData, {
        filename: 'sold-cards',
        includeDateInFilename: true,
      });
    } catch (error) {
      console.error('Error exporting sold items:', error);
      showIndexMessage('error', 'Failed to export sold items. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const navigate = useNavigate();
  const handleEditTransaction = (transaction: Transaction) => {
    if (!transaction.id) {
      showIndexMessage('error', 'Unable to edit this card.');
      return;
    }
    navigate(`/inventory/edit/${transaction.id}`);
  };

  const handleDeleteTransactionWithImages = async (id: string) => {
    const transaction = transactions.find(t => t.id === id) as any;
    if (transaction) {
      const originalIds = transaction._originalIds && transaction._originalIds.length > 0
        ? transaction._originalIds
        : [transaction.id];
      const transactionsToDelete = originalIds
        .map((transactionId: string) => transactions.find(t => t.id === transactionId))
        .filter(Boolean) as Transaction[];
      const imagePaths = transactionsToDelete.flatMap((t) => [
        t.front_image_path,
        t.back_image_path,
        ...(Array.isArray(t.other_image_paths) ? t.other_image_paths : []),
      ]);
      if (imagePaths.length > 0) {
        await deleteCardImages(imagePaths);
      }
    }
    await handleDeleteTransaction(id);
  };

  // Handler for deleting a transaction
  const handleDeleteTransaction = async (id: string) => {
    if (!dataService) {
      console.error('Data service not available');
      return;
    }

    try {
      // Check if this is a grouped transaction by looking in inventoryItems
      const groupedTransaction = inventoryItems.find((t: any) => {
        // Check if this transaction has the ID or if it's in the originalIds array
        return t.id === id || ((t as any)._originalIds && (t as any)._originalIds.includes(id));
      }) as any;
      
      if (groupedTransaction && groupedTransaction._originalIds && groupedTransaction._originalIds.length > 1) {
        // This is a grouped transaction - delete all transactions in the group
        const idsToDelete = groupedTransaction._originalIds;
        for (const originalId of idsToDelete) {
          await dataService.deleteTransaction(originalId);
        }
        
        // Update local state
        if (onTransactionsUpdate) {
          const idsToRemove = new Set(idsToDelete);
          const updatedTransactions = transactions.filter(t => !t.id || !idsToRemove.has(t.id));
          onTransactionsUpdate(updatedTransactions);
        }
        // Remove deleted row from selection so banner count stays correct (grouped row has one id in selectedItems)
        setSelectedItems((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      } else {
        // Single transaction delete
        await dataService.deleteTransaction(id);
        
        // Update local state instead of refreshing
        if (onTransactionsUpdate) {
          const updatedTransactions = transactions.filter(t => t.id !== id);
          onTransactionsUpdate(updatedTransactions);
        }
        // Remove deleted card from selection so banner shows updated count or hides when none left
        setSelectedItems((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    } catch (error) {
      console.error('Error deleting card:', error);
      showIndexMessage('error', 'Failed to delete card');
    }
  };

  // Handler for toggling lot mode
  const handleLotModeToggle = (checked: boolean) => {
    setIsLotMode(checked);
    if (checked) {
      // Initialize lot order data from current card data
      setLotOrderData({
        purchaseDate: newCardData.purchaseDate,
        purchasePlatform: newCardData.purchasePlatform,
        boughtFrom: newCardData.boughtFrom,
      });
      setLotCards([]);
      // Clear card-specific fields but keep order data
      setNewCardData(prev => ({
        ...prev,
        card: '',
        sport: '',
        purchasePrice: '',
        year: '',
        manufacturer: '',
        set: '',
        ...emptyCardDetailFields,
        other: '',
        grade: '',
        gradingOrderId: '',
        notes: '',
        numberOfCards: 1,
        frontImageFile: null,
        backImageFile: null,
        otherImageFiles: [],
      }));
    } else {
      // Restore card data fields from lot order data when disabling
      setNewCardData(prev => ({
        ...prev,
        purchaseDate: lotOrderData.purchaseDate || prev.purchaseDate,
        purchasePlatform: lotOrderData.purchasePlatform || prev.purchasePlatform,
        boughtFrom: lotOrderData.boughtFrom || prev.boughtFrom,
      }));
      // Clear lot data when disabling lot mode
      setLotOrderData({
        purchaseDate: '',
        purchasePlatform: '',
        boughtFrom: '',
      });
      setLotCards([]);
    }
  };

  const normalizeOtherImageFiles = (files: File[] = []) => {
    return files.filter(Boolean).slice(0, MAX_OTHER_IMAGES);
  };

  const uploadImagesForTransaction = async (
    transactionId: string,
    frontFile: File | null,
    backFile: File | null,
    otherFiles: File[]
  ) => {
    const updates: any = {};
    const normalizedOtherFiles = normalizeOtherImageFiles(otherFiles);

    if (frontFile) {
      updates.front_image_path = await uploadCardImage(frontFile, transactionId, "front");
    }
    if (backFile) {
      updates.back_image_path = await uploadCardImage(backFile, transactionId, "back");
    }
    if (normalizedOtherFiles.length > 0) {
      const otherPaths = await Promise.all(
        normalizedOtherFiles.map((file) => uploadCardImage(file, transactionId, "other"))
      );
      updates.other_image_paths = otherPaths;
    }

    return updates;
  };

  // Handler for adding a card to the lot (not to database yet)
  const handleAddCardToLot = () => {
    if (!newCardData.card || !lotOrderData.purchaseDate || !newCardData.purchasePrice) {
      showIndexMessage('error', 'Please fill in all required fields (Card Name, Purchase Date, Purchase Price)');
      return;
    }

    const numberOfCards = newCardData.numberOfCards || 1;
    const pricePerCard = parseFloat(newCardData.purchasePrice) || 0;
    
    // Create card entry(ies) for the lot
    for (let i = 0; i < numberOfCards; i++) {
      const cardData = {
        card: newCardData.card,
        sport: newCardData.sport || null,
        purchase_date: lotOrderData.purchaseDate,
        cost: pricePerCard,
        purchase_platform: lotOrderData.purchasePlatform || null,
        bought_from: lotOrderData.boughtFrom || null,
        grading_order_id: newCardData.gradingOrderId || null,
        grade: newCardData.grade || null,
        year: newCardData.year ? parseInt(newCardData.year) : null,
        manufacturer: newCardData.manufacturer || null,
        set: newCardData.set || null,
        card_number: newCardData.card_number || null,
        subject: newCardData.subject || null,
        league: newCardData.league || null,
        insert_name: newCardData.insert_name || null,
        parallel: newCardData.parallel || null,
        numbering: newCardData.numbering || null,
        other: newCardData.other || null,
        notes: newCardData.notes || null,
        frontImageFile: newCardData.frontImageFile,
        backImageFile: newCardData.backImageFile,
        otherImageFiles: normalizeOtherImageFiles(newCardData.otherImageFiles),
      };
      setLotCards(prev => [...prev, cardData]);
    }

    // Clear card-specific fields but keep order data
    setNewCardData(prev => ({
      ...prev,
      card: '',
      sport: '',
      purchasePrice: '',
      year: '',
      manufacturer: '',
      set: '',
      ...emptyCardDetailFields,
      other: '',
      grade: '',
      gradingOrderId: '',
      notes: '',
      numberOfCards: 1,
      frontImageFile: null,
      backImageFile: null,
      otherImageFiles: [],
    }));
  };

  // Handler for opening lot confirmation dialog
  const handleCompleteLot = () => {
    // Check if current form has valid data
    const hasValidFormData = newCardData.card && lotOrderData.purchaseDate && newCardData.purchasePrice;
    const hasLotCards = lotCards.length > 0;
    
    if (!hasValidFormData && !hasLotCards) {
      showIndexMessage('error', 'Please fill in all required fields (Card Name, Purchase Date, Price Per Card) or add cards to the lot.');
      return;
    }
    setLotConfirmationDialogOpen(true);
  };

  // Helper to get all cards that should be in the lot (includes current form data if valid)
  const getAllLotCards = () => {
    const allCards = [...lotCards];
    
    // If current form has valid data, include it
    if (newCardData.card && lotOrderData.purchaseDate && newCardData.purchasePrice) {
      const numberOfCards = newCardData.numberOfCards || 1;
      const pricePerCard = parseFloat(newCardData.purchasePrice) || 0;
      
      for (let i = 0; i < numberOfCards; i++) {
        const cardData = {
          card: newCardData.card,
          sport: newCardData.sport || null,
          purchase_date: lotOrderData.purchaseDate,
          cost: pricePerCard,
          purchase_platform: lotOrderData.purchasePlatform || null,
          bought_from: lotOrderData.boughtFrom || null,
          grading_order_id: newCardData.gradingOrderId || null,
          grade: newCardData.grade || null,
          year: newCardData.year ? parseInt(newCardData.year) : null,
          manufacturer: newCardData.manufacturer || null,
          set: newCardData.set || null,
          card_number: newCardData.card_number || null,
          subject: newCardData.subject || null,
          league: newCardData.league || null,
          insert_name: newCardData.insert_name || null,
          parallel: newCardData.parallel || null,
          numbering: newCardData.numbering || null,
          other: newCardData.other || null,
          notes: newCardData.notes || null,
          frontImageFile: newCardData.frontImageFile,
          backImageFile: newCardData.backImageFile,
          otherImageFiles: normalizeOtherImageFiles(newCardData.otherImageFiles),
        };
        allCards.push(cardData);
      }
    }
    
    return allCards;
  };

  // Handler for actually completing the lot (saving all cards)
  const handleConfirmCompleteLot = async () => {
    if (!dataService) {
      console.error('Data service not available');
      return;
    }

    try {
      // Get all cards including current form data
      const allCards = getAllLotCards();
      
      if (allCards.length === 0) {
        showIndexMessage('error', 'No cards to add. Please add at least one card.');
        return;
      }

      // Add all cards to the database
      for (const cardData of allCards) {
        const {
          frontImageFile,
          backImageFile,
          otherImageFiles,
          ...transactionData
        } = cardData as any;
        const result = await dataService.addTransaction(transactionData);
        const transactionId = (result as any)?.transaction_id || (result as any)?.data?.transaction_id;
        const hasImages = frontImageFile || backImageFile || (otherImageFiles && otherImageFiles.length > 0);
        if (transactionId && hasImages) {
          const imageUpdates = await uploadImagesForTransaction(
            transactionId,
            frontImageFile || null,
            backImageFile || null,
            otherImageFiles || []
          );
          if (Object.keys(imageUpdates).length > 0) {
            await dataService.updateTransaction(transactionId, imageUpdates);
          }
        }
      }

      // Update local state
      if (onTransactionsUpdate) {
        const allTransactions = await dataService.getTransactions();
        onTransactionsUpdate(allTransactions);
      }

      // Reset everything
      setLotCards([]);
      setLotOrderData({
        purchaseDate: '',
        purchasePlatform: '',
        boughtFrom: '',
      });
      setIsLotMode(false);
      setLotConfirmationDialogOpen(false);
      setAddCardDialogOpen(false);
      setNewCardData({
        card: '',
        sport: '',
        purchaseDate: '',
        purchasePlatform: '',
        purchasePrice: '',
        year: '',
        manufacturer: '',
        set: '',
        ...emptyCardDetailFields,
        other: '',
        grade: '',
        gradingOrderId: '',
        boughtFrom: '',
        notes: '',
        numberOfCards: 1,
        frontImageFile: null,
        backImageFile: null,
        otherImageFiles: [],
      });
    } catch (error) {
      console.error('Error completing lot:', error);
      showIndexMessage('error', 'Failed to add lot of cards. Please try again.');
    }
  };

  // Handler for closing add card dialog - reset lot mode
  const handleCloseAddCardDialog = () => {
    setAddCardDialogOpen(false);
    if (isLotMode) {
      // Reset lot mode when closing
      setIsLotMode(false);
      setLotCards([]);
      setLotOrderData({
        purchaseDate: '',
        purchasePlatform: '',
        boughtFrom: '',
      });
    }
  };

  // Handler for adding a new card (single card or completing lot)
  const addNewCard = async () => {
    if (!dataService) {
      console.error('Data service not available');
      return;
    }

    try {
      if (!newCardData.card || !newCardData.purchaseDate || !newCardData.purchasePrice) {
        showIndexMessage('error', 'Please fill in all required fields (Card Name, Purchase Date, Purchase Price)');
        return;
      }

      const numberOfCards = newCardData.numberOfCards || 1;
      const pricePerCard = parseFloat(newCardData.purchasePrice) || 0;
      const hasImages = newCardData.frontImageFile || newCardData.backImageFile || newCardData.otherImageFiles.length > 0;

      // Add single card or multiple if numberOfCards > 1
      for (let i = 0; i < numberOfCards; i++) {
        const transactionData = {
          card: newCardData.card,
          sport: newCardData.sport || null,
          purchase_date: newCardData.purchaseDate,
          cost: pricePerCard,
          purchase_platform: newCardData.purchasePlatform || null,
          grading_order_id: newCardData.gradingOrderId || null,
          grade: newCardData.grade || null,
          year: newCardData.year ? parseInt(newCardData.year) : null,
          manufacturer: newCardData.manufacturer || null,
          set: newCardData.set || null,
          card_number: newCardData.card_number || null,
          subject: newCardData.subject || null,
          league: newCardData.league || null,
          insert_name: newCardData.insert_name || null,
          parallel: newCardData.parallel || null,
          numbering: newCardData.numbering || null,
          other: newCardData.other || null,
          bought_from: newCardData.boughtFrom || null,
          notes: newCardData.notes || null,
        };

        const result = await dataService.addTransaction(transactionData);
        const transactionId = (result as any)?.transaction_id || (result as any)?.data?.transaction_id;
        if (transactionId && hasImages) {
          const imageUpdates = await uploadImagesForTransaction(
            transactionId,
            newCardData.frontImageFile,
            newCardData.backImageFile,
            newCardData.otherImageFiles
          );
          if (Object.keys(imageUpdates).length > 0) {
            await dataService.updateTransaction(transactionId, imageUpdates);
          }
        }
      }
      
      // Update local state instead of refreshing
      if (onTransactionsUpdate) {
        const allTransactions = await dataService.getTransactions();
        onTransactionsUpdate(allTransactions);
      }
      
      // Reset form
      setNewCardData({
        card: '',
        sport: '',
        purchaseDate: '',
        purchasePlatform: '',
        purchasePrice: '',
        year: '',
        manufacturer: '',
        set: '',
        ...emptyCardDetailFields,
        other: '',
        grade: '',
        gradingOrderId: '',
        boughtFrom: '',
        notes: '',
        numberOfCards: 1,
        frontImageFile: null,
        backImageFile: null,
        otherImageFiles: [],
      });
      setAddCardDialogOpen(false);
    } catch (error) {
      console.error('Error adding new card:', error);
      showIndexMessage('error', 'Failed to add new card');
    }
  };

  // Handler for opening move to grading dialog
  const handleMoveToGrading = (transaction: Transaction) => {
    // Check for grouped transaction metadata - try multiple ways to access it
    const tx = transaction as any;
    let count = tx._count || tx['_count'];
    
    // If not found directly, look it up in inventoryItems by group key
    if (!count || count <= 1) {
      const groupKey = getTransactionGroupKey(transaction);
      const groupedTx = inventoryItems.find(t => {
        const tKey = getTransactionGroupKey(t);
        return tKey === groupKey && (t as any)._count && (t as any)._count > 1;
      });
      if (groupedTx) {
        count = (groupedTx as any)._count;
        transaction = groupedTx; // Use the grouped version
      }
    }
    
    // If grouped (has _count > 1), show quantity selection dialog first
    if (count && count > 1) {
      if (!isTransactionEligibleForGrading(transaction)) {
        showIndexMessage('error', 'This card already has a grade or is currently out for grading.');
        return;
      }
      setPendingTransaction(transaction);
      setPendingAction('grading');
      setQuantityDialogOpen(true);
      return;
    }
    
    // Otherwise, proceed directly to grading dialog
    if (!isTransactionEligibleForGrading(transaction)) {
      showIndexMessage('error', 'This card already has a grade or is currently out for grading.');
      return;
    }
    setGradingTransaction(transaction);
    setGradingDialogStep('selectOrder');
    setSelectedGradingOrderId('');
    setNewOrderFromInventoryData({
      orderId: '',
      company: '',
      serviceLevel: '',
      submissionDate: '',
      cost: '',
      notes: '',
    });
    setGradingDialogOpen(true);
  };

  // Handler for proceeding to create new order
  const handleProceedToNewOrder = () => {
    if (!selectedGradingOrderId) {
      showIndexMessage('error', 'Please select a grading order or choose to create a new one');
      return;
    }
    
    if (selectedGradingOrderId === 'new') {
      setGradingDialogStep('newOrder');
    } else {
      // User selected an existing order, just save it
      handleSaveMoveToGrading();
    }
  };
  
  // Handler for saving move to grading (with existing order)
  const handleSaveMoveToGrading = async () => {
    if (!dataService || !gradingTransaction || !gradingTransaction.id) {
      console.error('Data service or transaction not available');
      return;
    }

    try {
      if (!selectedGradingOrderId || selectedGradingOrderId === 'new') {
        // Should not happen, but just in case
        return;
      }

      const updates: any = {
        grading_order_id: selectedGradingOrderId
      };

      const originalIds = (gradingTransaction as any)._originalIds || [gradingTransaction.id];
      const quantityToProcess = pendingQuantity > 0 ? pendingQuantity : 1;
      const idsToUpdate = originalIds.slice(0, quantityToProcess);

      // Update all selected transactions
      for (const id of idsToUpdate) {
        if (id) {
          await dataService.updateTransaction(id, updates);
        }
      }
      
      // Update local state instead of refreshing
      if (onTransactionsUpdate) {
        const updatedTransactions = transactions.map(t => {
          if (idsToUpdate.includes(t.id || '')) {
            return { ...t, ...updates, updated_at: new Date().toISOString() };
          }
          return t;
        });
        onTransactionsUpdate(updatedTransactions);
      }
      
      // Close dialog and reset
      setGradingDialogOpen(false);
      setGradingTransaction(null);
      setPendingQuantity(0);
      setSelectedGradingOrderId('');
      setGradingDialogStep('selectOrder');
    } catch (error) {
      console.error('Error moving card to grading:', error);
      showIndexMessage('error', 'Failed to move card to grading');
    }
  };
  
  // Handler for finalizing new grading order creation from inventory
  const handleFinalizeNewOrderFromInventory = async () => {
    if (!dataService || !gradingTransaction || !gradingTransaction.id) {
      console.error('Data service or transaction not available');
      return;
    }

    try {
      // First, create the grading order
      await dataService.addGradingOrder({
        order_id: newOrderFromInventoryData.orderId,
        company: newOrderFromInventoryData.company,
        grading_service_level: newOrderFromInventoryData.serviceLevel,
        submission_date: newOrderFromInventoryData.submissionDate,
        cost: parseFloat(newOrderFromInventoryData.cost || '0'),
        notes: newOrderFromInventoryData.notes || null,
      });
      
      // Then, update the cards to link them to this grading order
      const originalIds = (gradingTransaction as any)._originalIds || [gradingTransaction.id];
      const quantityToProcess = pendingQuantity > 0 ? pendingQuantity : 1;
      const idsToUpdate = originalIds.slice(0, quantityToProcess);

      const updates = {
        grading_order_id: newOrderFromInventoryData.orderId
      };

      for (const id of idsToUpdate) {
        if (id) {
          await dataService.updateTransaction(id, updates);
        }
      }
      
      // Update local state instead of refreshing
      if (onTransactionsUpdate) {
        const updatedTransactions = transactions.map(t => {
          if (idsToUpdate.includes(t.id || '')) {
            return { ...t, grading_order_id: newOrderFromInventoryData.orderId, updated_at: new Date().toISOString() };
          }
          return t;
        });
        onTransactionsUpdate(updatedTransactions);
      }
      
      // Update grading orders if callback provided
      if (onGradingOrdersUpdate) {
        const allGradingOrders = await dataService.getGradingOrders();
        onGradingOrdersUpdate(allGradingOrders);
      }
      
      // Close dialog and reset
      setGradingDialogOpen(false);
      setGradingTransaction(null);
      setPendingQuantity(0);
      setSelectedGradingOrderId('');
      setGradingDialogStep('selectOrder');
      setNewOrderFromInventoryData({
        orderId: '',
        company: '',
        serviceLevel: '',
        submissionDate: '',
        cost: '',
        notes: '',
      });
    } catch (error) {
      console.error('Error moving card to new grading order:', error);
      showIndexMessage('error', 'Failed to move card to new grading order');
    }
  };

  // Handler for opening mark as sold dialog
  const handleMarkAsSold = (transaction: Transaction) => {
    // Check for grouped transaction metadata - try multiple ways to access it
    const tx = transaction as any;
    let count = tx._count || tx['_count'];
    
    // If not found directly, look it up in inventoryItems by group key
    if (!count || count <= 1) {
      const groupKey = getTransactionGroupKey(transaction);
      const groupedTx = inventoryItems.find(t => {
        const tKey = getTransactionGroupKey(t);
        const hasCount = (t as any)._count && (t as any)._count > 1;
        const keyMatch = tKey === groupKey;
        return keyMatch && hasCount;
      });
      
      if (groupedTx) {
        count = (groupedTx as any)._count;
        transaction = groupedTx; // Use the grouped version
      }
    }
    
    // If grouped (has _count > 1), show quantity selection dialog first
    if (count && count > 1) {
      setPendingTransaction(transaction);
      setPendingAction('sell');
      setQuantityDialogOpen(true);
      return;
    }
    
    // Otherwise, proceed directly to bulk sold dialog (reusing bulk modal)
    // Set up as a single-item selection for the bulk dialog
    const singleTransactionMap = new Map<string, number>();
    if (transaction.id) {
      singleTransactionMap.set(transaction.id, 1);
    }
    initializeBulkSoldDataForSingleTransaction(transaction, singleTransactionMap);
  };

  // Handler for confirming quantity selection
  const handleQuantityConfirmed = (quantity: number) => {
    if (!pendingTransaction) return;
    
    // pendingQuantity is already set by onConfirm handler
    
    if (pendingAction === 'sell') {
      // Use bulk sold dialog instead of single-card dialog
      const quantitySelections = new Map<string, number>();
      if (pendingTransaction.id) {
        quantitySelections.set(pendingTransaction.id, quantity);
      }
      initializeBulkSoldDataForSingleTransaction(pendingTransaction, quantitySelections);
    } else if (pendingAction === 'grading') {
      if (!isTransactionEligibleForGrading(pendingTransaction)) {
        showIndexMessage('error', 'This card already has a grade or is currently out for grading.');
        setPendingTransaction(null);
        setPendingQuantity(0);
        return;
      }
      setGradingTransaction(pendingTransaction);
      setGradingDialogStep('selectOrder');
      setSelectedGradingOrderId('');
      setNewOrderFromInventoryData({
        orderId: '',
        company: '',
        serviceLevel: '',
        submissionDate: '',
        cost: '',
        notes: '',
      });
      setGradingDialogOpen(true);
    }
    
    // Don't clear pendingTransaction yet - we need it for processing
  };

  // Handler for saving mark as sold
  const handleSaveMarkAsSold = async () => {
    if (!dataService || !soldTransaction || !soldTransaction.id) {
      console.error('Data service or transaction not available');
      return;
    }

    try {
      if (!soldData.saleDate) {
        showIndexMessage('error', 'Please fill in sale date');
        return;
      }

      const originalIds = (soldTransaction as any)._originalIds || [soldTransaction.id];
      const quantityToProcess = pendingQuantity > 0 ? pendingQuantity : 1;
      const idsToUpdate = originalIds.slice(0, quantityToProcess);

      // Check if we're using individual prices (quantity > 1 and individual prices are set)
      const usingIndividualPrices = quantityToProcess > 1 && 
        Object.keys(soldData.individualSalePrices).length > 0 &&
        idsToUpdate.some((id: string) => soldData.individualSalePrices[id]);

      if (usingIndividualPrices) {
        // Validate individual prices
        const missingPrices = idsToUpdate.filter((id: string) => {
          const price = soldData.individualSalePrices[id];
          return !price || parseFloat(price) <= 0;
        });
        if (missingPrices.length > 0) {
          showIndexMessage('error', 'Please fill in sale price for all cards');
          return;
        }

        // Update each transaction with its individual price
        for (const id of idsToUpdate) {
          if (!id) continue;
          const updates: any = {
            sale_date: soldData.saleDate,
            sale_price: parseFloat(soldData.individualSalePrices[id as string] || '0'),
          };
          if (soldData.salePlatform) updates.sale_platform = soldData.salePlatform;
          if (soldData.soldTo) updates.sold_to = soldData.soldTo;
          if (soldData.individualMarketplaceFees[id as string]) {
            updates.marketplace_fees = parseFloat(soldData.individualMarketplaceFees[id as string] || '0');
          }
          if (soldData.individualShippingFees[id as string]) {
            updates.shipping_fees = parseFloat(soldData.individualShippingFees[id as string] || '0');
          }
          await dataService.updateTransaction(id, updates);
        }
      } else {
        // Single price for all (or single card)
        if (!soldData.salePrice) {
          showIndexMessage('error', 'Please fill in sale price');
          return;
        }

        const updates: any = {
          sale_date: soldData.saleDate,
          sale_price: parseFloat(soldData.salePrice) || 0,
        };
        if (soldData.salePlatform) updates.sale_platform = soldData.salePlatform;
        if (soldData.soldTo) updates.sold_to = soldData.soldTo;
        if (soldData.marketplaceFees) updates.marketplace_fees = parseFloat(soldData.marketplaceFees) || 0;
        if (soldData.shippingFees) updates.shipping_fees = parseFloat(soldData.shippingFees) || 0;

        // Update all selected transactions with the same price
        for (const id of idsToUpdate) {
          if (id) {
            await dataService.updateTransaction(id, updates);
          }
        }
      }
      
      // Update local state instead of refreshing
      if (onTransactionsUpdate) {
        const allTransactions = await dataService.getTransactions();
        onTransactionsUpdate(allTransactions);
      }
      
      // Close dialog and reset
      setSoldDialogOpen(false);
      setSoldTransaction(null);
      setPendingQuantity(0);
      setSoldData({ 
        saleDate: '', 
        salePrice: '', 
        salePlatform: '', 
        marketplaceFees: '0', 
        shippingFees: '0',
        soldTo: '',
        individualSalePrices: {},
        individualMarketplaceFees: {},
        individualShippingFees: {},
      });
    } catch (error) {
      console.error('Error marking card as sold:', error);
      showIndexMessage('error', 'Failed to mark card as sold');
    }
  };

  // Handler for selecting individual items
  const handleSelectItem = (id: string) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      const totalItems = inventoryItems.filter(t => t.id).length;
      setSelectAll(newSet.size === totalItems);
      return newSet;
    });
  };

  // Handler for select all
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedItems(new Set());
      setSelectAll(false);
    } else {
      const allIds = new Set(inventoryItems.map(t => t.id).filter((id): id is string => !!id));
      setSelectedItems(allIds);
      setSelectAll(true);
    }
  };

  // Handler for bulk move to grading
  const handleBulkMoveToGrading = () => {
    if (selectedItems.size === 0) {
      showIndexMessage('error', 'Please select cards to move to grading');
      return;
    }

    if (hasIneligibleSelection) {
      showIndexMessage('error', 'One or more selected cards already have a grade or are currently out for grading. Please deselect those cards before continuing.');
      return;
    }
    
    // Check for grouped transactions
    const groupedTransactions = selectedTransactions.filter(t => {
      const count = (t as any)._count;
      return count && count > 1;
    });
    
    if (groupedTransactions.length > 0) {
      // Need to collect quantity selections for grouped transactions
      setBulkGradingQuantitySelections(new Map());
      setBulkGradingQuantityTransactionsQueue([...groupedTransactions]);
      setCurrentBulkGradingQuantityTransaction(groupedTransactions[0]);
      setBulkGradingQuantityDialogOpen(true);
      return;
    }
    
    // No grouped transactions, proceed directly
    // Calculate total count (all single transactions)
    setBulkGradingTotalCardCount(selectedItems.size);
    setBulkGradingDialogStep('selectOrder');
    setBulkSelectedGradingOrderId('');
    setNewOrderFromBulkData({
      orderId: '',
      company: '',
      serviceLevel: '',
      submissionDate: '',
      cost: '',
      notes: '',
    });
    setBulkGradingDialogOpen(true);
  };
  
  // Handler for bulk grading quantity selection confirmation
  const handleBulkGradingQuantityConfirmed = (quantity: number) => {
    if (!currentBulkGradingQuantityTransaction) return;
    
    // Store the quantity selection
    const newSelections = new Map(bulkGradingQuantitySelections);
    newSelections.set(currentBulkGradingQuantityTransaction.id || '', quantity);
    setBulkGradingQuantitySelections(newSelections);
    
    // Move to next grouped transaction
    const remainingQueue = bulkGradingQuantityTransactionsQueue.slice(1);
    setBulkGradingQuantityTransactionsQueue(remainingQueue);
    
    if (remainingQueue.length > 0) {
      // Show next quantity dialog - use setTimeout to ensure state updates
      setTimeout(() => {
        setCurrentBulkGradingQuantityTransaction(remainingQueue[0]);
        setBulkGradingQuantityDialogOpen(true); // Ensure dialog stays open
      }, 0);
    } else {
      // All quantity selections done, calculate total count and open grading dialog
      // Calculate total cards after expanding grouped transactions with selections
      let totalCards = 0;
      selectedTransactions.forEach(t => {
        const count = (t as any)._count;
        if (count && count > 1) {
          // Grouped transaction - use quantity selection
          const selectedQuantity = newSelections.get(t.id || '') || count;
          totalCards += selectedQuantity;
        } else {
          // Single transaction
          totalCards += 1;
        }
      });
      
      setBulkGradingTotalCardCount(totalCards);
      // Store the quantity selections for use in save handlers
      setStoredBulkGradingQuantitySelections(newSelections);
      setBulkGradingQuantityDialogOpen(false);
      setCurrentBulkGradingQuantityTransaction(null);
      setBulkGradingDialogStep('selectOrder');
      setBulkSelectedGradingOrderId('');
      setNewOrderFromBulkData({
        orderId: '',
        company: '',
        serviceLevel: '',
        submissionDate: '',
        cost: '',
        notes: '',
      });
      setBulkGradingDialogOpen(true);
    }
  };

  // Handler for bulk mark as sold
  const handleBulkMarkAsSold = () => {
    if (selectedItems.size === 0) {
      showIndexMessage('error', 'Please select cards to mark as sold');
      return;
    }
    
    // Check for grouped transactions
    const groupedTransactions = selectedTransactions.filter(t => {
      const count = (t as any)._count;
      return count && count > 1;
    });
    
    if (groupedTransactions.length > 0) {
      // Need to collect quantity selections for grouped transactions
      setBulkQuantitySelections(new Map());
      setBulkQuantityTransactionsQueue([...groupedTransactions]);
      setCurrentBulkQuantityTransaction(groupedTransactions[0]);
      setBulkQuantityDialogOpen(true);
      return;
    }
    
    // No grouped transactions, proceed directly
    initializeBulkSoldData();
  };
  
  // Initialize bulk sold data for a single transaction (from 3-dot menu)
  const initializeBulkSoldDataForSingleTransaction = (transaction: Transaction, quantitySelections: Map<string, number>) => {
    // Get all individual transaction IDs (expanding grouped transactions)
    const allTransactionIds: string[] = [];
    const transactionIdToTransactionMap = new Map<string, Transaction>();
    
    const count = (transaction as any)._count;
    if (count && count > 1) {
      // Grouped transaction - use quantity selection
      const quantity = quantitySelections.get(transaction.id || '') || count;
      const originalIds = (transaction as any)._originalIds || [transaction.id];
      const idsToAdd = originalIds.slice(0, quantity);
      idsToAdd.forEach((id: string) => {
        if (id) {
          allTransactionIds.push(id);
          // Map each ID to the transaction for display
          transactionIdToTransactionMap.set(id, transaction);
        }
      });
    } else {
      // Single transaction
      if (transaction.id) {
        allTransactionIds.push(transaction.id);
        transactionIdToTransactionMap.set(transaction.id, transaction);
      }
    }
    
    // Initialize individual prices for each transaction ID
    const initialPrices: Record<string, string> = {};
    const initialMarketplaceFees: Record<string, string> = {};
    const initialShippingFees: Record<string, string> = {};
    
    allTransactionIds.forEach((id: string) => {
      initialPrices[id] = '';
      initialMarketplaceFees[id] = '';
      initialShippingFees[id] = '';
    });
    
    // Store the count and transaction map for the dialog
    setBulkSoldData({
      saleDate: '',
      salePlatform: '',
      soldTo: '',
      splitPriceEqually: false,
      totalSalePrice: '',
      individualSalePrices: initialPrices,
      splitFeesEqually: false,
      totalMarketplaceFees: '',
      totalShippingFees: '',
      individualMarketplaceFees: initialMarketplaceFees,
      individualShippingFees: initialShippingFees,
      _totalCardCount: allTransactionIds.length, // Store count for dialog title
      _transactionIdMap: transactionIdToTransactionMap, // Store map for rendering
      _quantitySelections: quantitySelections, // Store selections for save handler
      _isSingleTransaction: true, // Flag to indicate this is from 3-dot menu
      _singleTransaction: transaction, // Store the original transaction
    });
    setBulkSoldDialogOpen(true);
  };

  // Initialize bulk sold data with all selected transaction IDs
  const initializeBulkSoldData = (quantitySelections?: Map<string, number>) => {
    // Use provided selections or fall back to state
    const selectionsToUse = quantitySelections || bulkQuantitySelections;
    
    // Get all individual transaction IDs (expanding grouped transactions)
    const allTransactionIds: string[] = [];
    const transactionIdToTransactionMap = new Map<string, Transaction>();
    
    selectedTransactions.forEach(t => {
      const count = (t as any)._count;
      if (count && count > 1) {
        // Grouped transaction - use quantity selection
        const quantity = selectionsToUse.get(t.id || '') || count;
        const originalIds = (t as any)._originalIds || [t.id];
        const idsToAdd = originalIds.slice(0, quantity);
        idsToAdd.forEach((id: string) => {
          if (id) {
            allTransactionIds.push(id);
            // Map each ID to the transaction for display
            transactionIdToTransactionMap.set(id, t);
          }
        });
      } else {
        // Single transaction
        if (t.id) {
          allTransactionIds.push(t.id);
          transactionIdToTransactionMap.set(t.id, t);
        }
      }
    });
    
    // Initialize individual prices for each transaction ID
    const initialPrices: Record<string, string> = {};
    const initialMarketplaceFees: Record<string, string> = {};
    const initialShippingFees: Record<string, string> = {};
    
    allTransactionIds.forEach((id: string) => {
      initialPrices[id] = '';
      initialMarketplaceFees[id] = '';
      initialShippingFees[id] = '';
    });
    
    // Store the count and transaction map for the dialog
    setBulkSoldData({
      saleDate: '',
      salePlatform: '',
      soldTo: '',
      splitPriceEqually: false,
      totalSalePrice: '',
      individualSalePrices: initialPrices,
      splitFeesEqually: false,
      totalMarketplaceFees: '',
      totalShippingFees: '',
      individualMarketplaceFees: initialMarketplaceFees,
      individualShippingFees: initialShippingFees,
      _totalCardCount: allTransactionIds.length, // Store count for dialog title
      _transactionIdMap: transactionIdToTransactionMap, // Store map for rendering
      _quantitySelections: selectionsToUse, // Store selections for save handler
    });
    setBulkSoldDialogOpen(true);
  };
  
  // Handler for bulk quantity selection confirmation
  const handleBulkQuantityConfirmed = (quantity: number) => {
    if (!currentBulkQuantityTransaction) return;
    
    // Store the quantity selection
    const newSelections = new Map(bulkQuantitySelections);
    newSelections.set(currentBulkQuantityTransaction.id || '', quantity);
    setBulkQuantitySelections(newSelections);
    
    // Move to next grouped transaction
    const remainingQueue = bulkQuantityTransactionsQueue.slice(1);
    setBulkQuantityTransactionsQueue(remainingQueue);
    
    if (remainingQueue.length > 0) {
      // Show next quantity dialog - use setTimeout to ensure state updates
      setTimeout(() => {
        setCurrentBulkQuantityTransaction(remainingQueue[0]);
        setBulkQuantityDialogOpen(true); // Ensure dialog stays open
      }, 0);
    } else {
      // All quantity selections done, initialize bulk sold data
      // Pass the updated selections directly to avoid async state issues
      setBulkQuantityDialogOpen(false);
      setCurrentBulkQuantityTransaction(null);
      initializeBulkSoldData(newSelections);
    }
  };

  // Handler for proceeding to bulk create new order
  const handleBulkProceedToNewOrder = () => {
    if (!bulkSelectedGradingOrderId) {
      showIndexMessage('error', 'Please select a grading order or choose to create a new one');
      return;
    }
    
    if (bulkSelectedGradingOrderId === 'new') {
      setBulkGradingDialogStep('newOrder');
    } else {
      // User selected an existing order, just save it
      handleSaveBulkMoveToGrading();
    }
  };
  
  // Handler for saving bulk move to grading (with existing order)
  const handleSaveBulkMoveToGrading = async () => {
    if (!dataService) {
      console.error('Data service not available');
      return;
    }

    if (!bulkSelectedGradingOrderId || bulkSelectedGradingOrderId === 'new') {
      // Should not happen, but just in case
      return;
    }

    try {
      const updates: any = {
        grading_order_id: bulkSelectedGradingOrderId
      };

      // Get all individual transaction IDs (expanding grouped transactions)
      // Use stored selections instead of state to avoid async issues
      const selectionsToUse = storedBulkGradingQuantitySelections.size > 0 
        ? storedBulkGradingQuantitySelections 
        : bulkGradingQuantitySelections;
      const allTransactionIds: string[] = [];
      selectedTransactions.forEach(t => {
        const count = (t as any)._count;
        if (count && count > 1) {
          // Grouped transaction - use quantity selection
          const quantity = selectionsToUse.get(t.id || '') || count;
          const originalIds = (t as any)._originalIds || [t.id];
          allTransactionIds.push(...originalIds.slice(0, quantity));
        } else {
          // Single transaction
          if (t.id) allTransactionIds.push(t.id);
        }
      });

      // Update all selected items
      let successCount = 0;

      for (const transactionId of allTransactionIds) {
        if (!transactionId) {
          continue;
        }

        const transaction = transactions.find(t => t.id === transactionId);
        if (!transaction || !isTransactionEligibleForGrading(transaction)) {
          continue;
        }

        try {
          await dataService.updateTransaction(transactionId, updates);
          successCount++;
        } catch (error) {
          console.error(`Error updating transaction ${transactionId}:`, error);
          // Continue with other items even if one fails
        }
      }

      // Update local state instead of refreshing
      if (onTransactionsUpdate && successCount > 0) {
        // Fetch updated transactions to get latest data
        const allTransactions = await dataService.getTransactions();
        onTransactionsUpdate(allTransactions);
      }

      // Close dialog and clear selection
      setBulkGradingDialogOpen(false);
      setBulkSelectedGradingOrderId('');
      setBulkGradingDialogStep('selectOrder');
      setStoredBulkGradingQuantitySelections(new Map());
      clearSelection();
    } catch (error) {
      console.error('Error moving cards to grading:', error);
      showIndexMessage('error', 'Failed to move cards to grading');
    }
  };
  
  // Handler for finalizing bulk new grading order creation
  const handleFinalizeBulkNewOrder = async () => {
    if (!dataService) {
      console.error('Data service not available');
      return;
    }

    try {
      // First, create the grading order
      await dataService.addGradingOrder({
        order_id: newOrderFromBulkData.orderId,
        company: newOrderFromBulkData.company,
        grading_service_level: newOrderFromBulkData.serviceLevel,
        submission_date: newOrderFromBulkData.submissionDate,
        cost: parseFloat(newOrderFromBulkData.cost || '0'),
        notes: newOrderFromBulkData.notes || null,
      });
      
      // Then, update all selected cards to link them to this grading order
      const updates: any = {
        grading_order_id: newOrderFromBulkData.orderId
      };
      
      // Get all individual transaction IDs (expanding grouped transactions)
      // Use stored selections instead of state to avoid async issues
      const selectionsToUse = storedBulkGradingQuantitySelections.size > 0 
        ? storedBulkGradingQuantitySelections 
        : bulkGradingQuantitySelections;
      const allTransactionIds: string[] = [];
      selectedTransactions.forEach(t => {
        const count = (t as any)._count;
        if (count && count > 1) {
          // Grouped transaction - use quantity selection
          const quantity = selectionsToUse.get(t.id || '') || count;
          const originalIds = (t as any)._originalIds || [t.id];
          allTransactionIds.push(...originalIds.slice(0, quantity));
        } else {
          // Single transaction
          if (t.id) allTransactionIds.push(t.id);
        }
      });
      
      let successCount = 0;

      for (const transactionId of allTransactionIds) {
        if (!transactionId) {
          continue;
        }

        const transaction = transactions.find(t => t.id === transactionId);
        if (!transaction || !isTransactionEligibleForGrading(transaction)) {
          continue;
        }

        try {
          await dataService.updateTransaction(transactionId, updates);
          successCount++;
        } catch (error) {
          console.error(`Error updating transaction ${transactionId}:`, error);
          // Continue with other items even if one fails
        }
      }

      // Update local state instead of refreshing
      if (onTransactionsUpdate && successCount > 0) {
        // Fetch updated transactions to get latest data
        const allTransactions = await dataService.getTransactions();
        onTransactionsUpdate(allTransactions);
      }
      
      // Update grading orders if callback provided
      if (onGradingOrdersUpdate) {
        const allGradingOrders = await dataService.getGradingOrders();
        onGradingOrdersUpdate(allGradingOrders);
      }
      
      // Close dialog and clear selection
      setBulkGradingDialogOpen(false);
      setBulkSelectedGradingOrderId('');
      setBulkGradingDialogStep('selectOrder');
      setStoredBulkGradingQuantitySelections(new Map());
      setNewOrderFromBulkData({
        orderId: '',
        company: '',
        serviceLevel: '',
        submissionDate: '',
        cost: '',
        notes: '',
      });
      clearSelection();
    } catch (error) {
      console.error('Error moving cards to new grading order:', error);
      showIndexMessage('error', 'Failed to move cards to new grading order');
    }
  };

  // Handler for saving bulk mark as sold
  const handleSaveBulkMarkAsSold = async () => {
    if (!dataService) {
      console.error('Data service not available');
      return;
    }

    if (!bulkSoldData.saleDate) {
      showIndexMessage('error', 'Please fill in sale date');
      return;
    }

    // Get all individual transaction IDs first (expanding grouped transactions)
    // Use stored selections from bulkSoldData if available, otherwise fall back to state
    const selectionsToUse = bulkSoldData._quantitySelections || bulkQuantitySelections;
    const allTransactionIds: string[] = [];
    selectedTransactions.forEach(t => {
      const count = (t as any)._count;
      if (count && count > 1) {
        // Grouped transaction - use quantity selection
        const quantity = selectionsToUse.get(t.id || '') || count;
        const originalIds = (t as any)._originalIds || [t.id];
        allTransactionIds.push(...originalIds.slice(0, quantity));
      } else {
        // Single transaction
        if (t.id) allTransactionIds.push(t.id);
      }
    });

    // Validate that all cards have prices if not splitting equally
    if (!bulkSoldData.splitPriceEqually) {
      const missingPrices = allTransactionIds.filter(id => {
        const price = bulkSoldData.individualSalePrices[id];
        return !price || parseFloat(price) <= 0;
      });
      if (missingPrices.length > 0) {
        showIndexMessage('error', 'Please fill in sale price for all cards');
        return;
      }
    } else {
      if (!bulkSoldData.totalSalePrice || parseFloat(bulkSoldData.totalSalePrice) <= 0) {
        showIndexMessage('error', 'Please fill in total sale price');
        return;
      }
    }

    try {
      // Calculate price per card if splitting equally
      const pricePerCard = bulkSoldData.splitPriceEqually && bulkSoldData.totalSalePrice
        ? parseFloat(bulkSoldData.totalSalePrice) / allTransactionIds.length
        : 0;

      // Calculate fees per card if splitting equally
      const marketplaceFeePerCard = bulkSoldData.splitFeesEqually && bulkSoldData.totalMarketplaceFees
        ? parseFloat(bulkSoldData.totalMarketplaceFees) / allTransactionIds.length
        : 0;
      const shippingFeePerCard = bulkSoldData.splitFeesEqually && bulkSoldData.totalShippingFees
        ? parseFloat(bulkSoldData.totalShippingFees) / allTransactionIds.length
        : 0;

      // Update all selected items
      let successCount = 0;

      for (const transactionId of allTransactionIds) {
        if (!transactionId) {
          continue;
        }

        // Find the transaction to check if already sold
        const transaction = transactions.find(t => t.id === transactionId);
        if (!transaction) continue;

        // Skip if transaction is already sold
        const saleDate = transaction.sale_date;
        const alreadySold = saleDate && (typeof saleDate === 'string' ? saleDate.trim() !== '' : Boolean(saleDate));
        if (alreadySold) {
          continue;
        }

        const updates: any = {
          sale_date: bulkSoldData.saleDate,
        };

        // Set sale price
        if (bulkSoldData.splitPriceEqually) {
          updates.sale_price = pricePerCard;
        } else {
          const price = bulkSoldData.individualSalePrices[transactionId];
          updates.sale_price = parseFloat(price || '0');
        }

        // Set platform
        if (bulkSoldData.salePlatform) {
          updates.sale_platform = bulkSoldData.salePlatform;
        }

        // Set sold to
        if (bulkSoldData.soldTo) {
          updates.sold_to = bulkSoldData.soldTo;
        }

        // Set fees
        if (bulkSoldData.splitFeesEqually) {
          updates.marketplace_fees = marketplaceFeePerCard;
          updates.shipping_fees = shippingFeePerCard;
        } else {
          const marketplaceFee = bulkSoldData.individualMarketplaceFees[transactionId];
          const shippingFee = bulkSoldData.individualShippingFees[transactionId];
          updates.marketplace_fees = marketplaceFee ? parseFloat(marketplaceFee) : 0;
          updates.shipping_fees = shippingFee ? parseFloat(shippingFee) : 0;
        }

        try {
          await dataService.updateTransaction(transactionId, updates);
          successCount++;
        } catch (error) {
          console.error(`Error updating transaction ${transactionId}:`, error);
          // Continue with other items even if one fails
        }
      }

      // Update local state instead of refreshing
      if (onTransactionsUpdate && successCount > 0) {
        // Fetch updated transactions to get latest data
        const allTransactions = await dataService.getTransactions();
        onTransactionsUpdate(allTransactions);
      }

      // Close dialog and clear selection
      setBulkSoldDialogOpen(false);
      setBulkSoldData({
        saleDate: '',
        salePlatform: '',
        soldTo: '',
        splitPriceEqually: false,
        totalSalePrice: '',
        individualSalePrices: {},
        splitFeesEqually: false,
        totalMarketplaceFees: '',
        totalShippingFees: '',
        individualMarketplaceFees: {},
        individualShippingFees: {},
        _totalCardCount: undefined,
        _transactionIdMap: undefined,
        _quantitySelections: undefined,
        _isSingleTransaction: undefined,
        _singleTransaction: undefined,
      });
      clearSelection();
    } catch (error) {
      console.error('Error marking cards as sold:', error);
      showIndexMessage('error', 'Failed to mark cards as sold');
    }
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedItems(new Set());
    setSelectAll(false);
  };

  // Handler for sorting
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field as any);
      setSortDirection('asc');
    }
  };

  // Handler for opening column picker
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleOpenColumnPicker = (table: 'inventory' | 'sold') => {
    setActiveColumnPicker(table);
    setColumnPickerOpen(true);
  };

  // Handler for column visibility change
  const handleColumnVisibilityChange = (visibleColumns: string[], columnOrder: string[]) => {
    if (activeColumnPicker === 'inventory') {
      setInventoryVisibleColumns(visibleColumns);
      setInventoryColumnOrder(columnOrder);
    } else {
      setSoldVisibleColumns(visibleColumns);
      setSoldColumnOrder(columnOrder);
    }
  };

  // Handler for applying filters
  const handleApplyFilters = (filters: FilterOptions) => {
    setActiveFilters(filters);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header and Navigation are now in App.tsx */}
      
      <main className="container py-8 space-y-8">
        {/* Inventory Management Section */}
        <div className="space-y-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-3xl font-bold tracking-tight">Inventory Management</h2>
              <p className="text-muted-foreground mt-1">
                Current Inventory ({itemsInInventory} items, {formatCurrency(totalInventoryValue)} value)
              </p>
            </div>
            <Button variant="gradient" size="sm" className="gap-2" onClick={() => navigate("/inventory/new")}>
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Add New Card</span>
              <span className="sm:hidden">Add</span>
            </Button>
          </div>

          {/* Inline success/error message */}
          {indexMessage && (
            <div className={`flex items-center gap-3 p-4 rounded-lg border ${
              indexMessage.type === "success" ? "bg-success/10 border-success/20" : "bg-destructive/10 border-destructive/20"
            }`}>
              {indexMessage.type === "success" ? (
                <div className="h-8 w-8 rounded-full bg-success/20 flex items-center justify-center flex-shrink-0">
                  <Check className="h-5 w-5 text-success" />
                </div>
              ) : (
                <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0" />
              )}
              <p className={`flex-1 text-sm ${indexMessage.type === "success" ? "text-success" : "text-destructive"}`}>
                {indexMessage.text}
              </p>
              <Button variant="ghost" size="sm" className="flex-shrink-0" onClick={() => setIndexMessage(null)} aria-label="Dismiss">
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Stats Grid */}
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              title="Total Inventory Value"
              value={formatCurrency(totalInventoryValue)}
              change={`${transactions.length} items`}
              changeType="positive"
              icon={Package}
              gradient="primary"
            />
            <StatCard
              title="Total Sales"
              value={totalSales.toString()}
              change={`${transactions.length - totalSales} remaining`}
              changeType="positive"
              icon={TrendingUp}
              gradient="secondary"
            />
            <StatCard
              title="Net Profit"
              value={formatCurrency(totalProfit)}
              change="From sales"
              changeType={totalProfit >= 0 ? "positive" : "negative"}
              icon={DollarSign}
              gradient="tertiary"
            />
            <StatCard
              title="Items in Inventory"
              value={itemsInInventory.toString()}
              change={`${totalSales} sold`}
              changeType="neutral"
              icon={Activity}
              gradient="primary"
            />
          </div>

          {/* Search Bar and Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search cards, sports, platforms, grades..."
                className="pl-10 glass-card"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
              >
                <span className="hidden sm:inline">Select All</span>
                <span className="sm:hidden">All</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportInventory}
                disabled={isExporting || inventoryItems.length === 0}
                className="gap-2"
              >
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline">Download Spreadsheet</span>
                <span className="sm:hidden">Export</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setQuickFiltersOpen(true)}
                className="gap-2"
              >
                <Filter className="h-4 w-4" />
                <span className="hidden sm:inline">Filters</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setActiveColumnPicker('inventory');
                  setColumnPickerOpen(true);
                }}
                className="gap-2"
              >
                <Settings2 className="h-4 w-4" />
                <span className="hidden sm:inline">Columns</span>
              </Button>
            </div>
          </div>
          
          {/* Bulk Actions Bar */}
          {selectedItems.size > 0 && (
            <div className="glass-card rounded-lg p-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between animate-fade-in sticky top-16 z-40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-sm">
                  {selectedItems.size} selected
                </Badge>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleBulkMoveToGrading}
                  className="gap-2"
                  disabled={hasIneligibleSelection}
                  title={hasIneligibleSelection ? 'Deselect graded or in-progress cards before moving to grading' : undefined}
                >
                  <Package className="h-4 w-4" />
                  <span className="hidden sm:inline">Move to Grading</span>
                  <span className="sm:hidden">Grading</span>
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setBulkDeleteDialogOpen(true)}
                  className="gap-2 text-destructive focus:text-destructive hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                  <span className="hidden sm:inline">Delete</span>
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleBulkMarkAsSold}
                  className="gap-2"
                >
                  <TrendingUp className="h-4 w-4" />
                  <span className="hidden sm:inline">Mark as Sold</span>
                  <span className="sm:hidden">Sold</span>
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={clearSelection}
                >
                  Clear
                </Button>
              </div>
            </div>
          )}

          {/* Inventory Table */}
          <InventoryTable 
            transactions={inventoryItems}
            selectedItems={selectedItems}
            onSelectItem={handleSelectItem}
            onSelectAll={handleSelectAll}
            selectAll={selectAll}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            visibleColumns={inventoryVisibleColumns}
            columnOrder={inventoryColumnOrder}
            cardImagePreviewBehavior={cardImagePreviewBehavior}
            cardImagePreviewSize={cardImagePreviewSize}
            onEditTransaction={(transaction) => {
              handleEditTransaction(transaction);
            }}
            onDeleteTransaction={(id) => setCardToDeleteId(id)}
            onMoveToGrading={(transaction) => {
              handleMoveToGrading(transaction);
            }}
            onAddNewCard={() => navigate("/inventory/new")}
            onMarkAsSold={(transaction) => {
              handleMarkAsSold(transaction);
            }}
            canMoveToGrading={isTransactionEligibleForGrading}
          />
          
          {/* Sold Items Table */}
          {soldItems.length > 0 && (
            <div className="mt-8">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold tracking-tight">Sold Cards</h2>
                  <p className="text-muted-foreground mt-1">
                    Recently sold cards ({soldItems.length} items)
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExportSold}
                    disabled={isExporting || soldItems.length === 0}
                    className="gap-2"
                  >
                    <Download className="h-4 w-4" />
                    <span className="hidden sm:inline">Download Spreadsheet</span>
                    <span className="sm:hidden">Export</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setQuickFiltersOpen(true)}
                    className="gap-2"
                  >
                    <Filter className="h-4 w-4" />
                    <span className="hidden sm:inline">Filters</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setActiveColumnPicker('sold');
                      setColumnPickerOpen(true);
                    }}
                    className="gap-2"
                  >
                    <Settings2 className="h-4 w-4" />
                    <span className="hidden sm:inline">Columns</span>
                  </Button>
                </div>
              </div>
              
              <InventoryTable 
                transactions={soldItems}
                isSoldTable={true}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
                visibleColumns={soldVisibleColumns}
                columnOrder={soldColumnOrder}
                cardImagePreviewBehavior={cardImagePreviewBehavior}
                cardImagePreviewSize={cardImagePreviewSize}
                onEditTransaction={(transaction) => {
                  handleEditTransaction(transaction);
                }}
                onDeleteTransaction={(id) => setCardToDeleteId(id)}
              />
            </div>
          )}
        </div>
      </main>
      
      {/* Add New Card Dialog */}
      <Dialog open={addCardDialogOpen} onOpenChange={handleCloseAddCardDialog}>
        <DialogContent className="flex max-h-[min(92vh,880px)] w-[min(96vw,42rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-none">
          <DialogHeader className="shrink-0 space-y-2 border-b border-border px-6 py-4 text-left">
            <DialogTitle>Add New Card{isLotMode ? ' (Lot Mode)' : ''}</DialogTitle>
            <DialogDescription>
              {isLotMode 
                ? `Adding multiple cards from the same purchase. You've added ${lotCards.length} card(s) to this lot.`
                : 'Add a new card to your inventory. Fields marked with * are required.'
              }
            </DialogDescription>
          </DialogHeader>
          
          {/* Lot Mode Toggle */}
          <div className="flex shrink-0 items-center space-x-2 border-b border-border px-6 py-3">
            <Checkbox 
              id="lot-mode" 
              checked={isLotMode}
              onCheckedChange={handleLotModeToggle}
            />
            <Label htmlFor="lot-mode" className="text-sm font-medium cursor-pointer">
              Adding a lot of cards
            </Label>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          {isLotMode ? (
            <>
              {/* Lot Mode: Order-Specific Fields (Top Section) */}
              <div className="space-y-4 pb-4 border-b">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Order Information</h3>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="lot-purchaseDate">Purchase Date *</Label>
                    <Input
                      id="lot-purchaseDate"
                      type="date"
                      value={lotOrderData.purchaseDate}
                      onChange={(e) => setLotOrderData(prev => ({ ...prev, purchaseDate: e.target.value }))}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="lot-purchasePlatform">Purchase Platform</Label>
                    <Input
                      id="lot-purchasePlatform"
                      value={lotOrderData.purchasePlatform}
                      onChange={(e) => setLotOrderData(prev => ({ ...prev, purchasePlatform: e.target.value }))}
                      placeholder="e.g., eBay, COMC, WhatNot"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="lot-boughtFrom">Bought From</Label>
                    <Input
                      id="lot-boughtFrom"
                      value={lotOrderData.boughtFrom}
                      onChange={(e) => setLotOrderData(prev => ({ ...prev, boughtFrom: e.target.value }))}
                      placeholder="e.g., Seller name, Store name"
                    />
                  </div>
                </div>
              </div>

              {/* Lot Mode: Card-Specific Fields */}
              <div className="space-y-4 pt-4">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Card Information</h3>
                <Tabs defaultValue="essentials" className="w-full">
                  <TabsList className="grid w-full grid-cols-2 sm:inline-flex">
                    <TabsTrigger value="essentials">Essentials</TabsTrigger>
                    <TabsTrigger value="details">Details</TabsTrigger>
                  </TabsList>
                  <TabsContent value="essentials" className="mt-4 space-y-4">
                    <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-4">
                      <p className="text-sm font-medium text-foreground">Photos</p>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="lot-front-image" className="text-sm font-normal">Front</Label>
                          <Input
                            id="lot-front-image"
                            type="file"
                            accept="image/*"
                            onChange={(e) => setNewCardData(prev => ({ ...prev, frontImageFile: e.target.files?.[0] || null }))}
                          />
                          {newCardData.frontImageFile && (
                            <p className="text-xs text-muted-foreground">{newCardData.frontImageFile.name}</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="lot-back-image" className="text-sm font-normal">Back</Label>
                          <Input
                            id="lot-back-image"
                            type="file"
                            accept="image/*"
                            onChange={(e) => setNewCardData(prev => ({ ...prev, backImageFile: e.target.files?.[0] || null }))}
                          />
                          {newCardData.backImageFile && (
                            <p className="text-xs text-muted-foreground">{newCardData.backImageFile.name}</p>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lot-other-images" className="text-sm font-normal">Other (up to 3)</Label>
                        <Input
                          id="lot-other-images"
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []).slice(0, MAX_OTHER_IMAGES);
                            setNewCardData(prev => ({ ...prev, otherImageFiles: files }));
                          }}
                        />
                        {newCardData.otherImageFiles.length > 0 && (
                          <p className="text-xs text-muted-foreground">{newCardData.otherImageFiles.length} selected</p>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lot-card">Card name *</Label>
                      <Input
                        id="lot-card"
                        value={newCardData.card}
                        onChange={(e) => setNewCardData(prev => ({ ...prev, card: e.target.value }))}
                        placeholder="e.g., 2023 Topps Chrome — name as you want it listed"
                      />
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="lot-purchasePrice">Price per card *</Label>
                        <Input
                          id="lot-purchasePrice"
                          type="number"
                          step="0.01"
                          value={newCardData.purchasePrice}
                          onChange={(e) => setNewCardData(prev => ({ ...prev, purchasePrice: e.target.value }))}
                          placeholder="0.00"
                        />
                        {newCardData.numberOfCards > 1 && (
                          <p className="text-sm text-muted-foreground">
                            Total: ${(parseFloat(newCardData.purchasePrice || "0") * (newCardData.numberOfCards || 1)).toFixed(2)}
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lot-numberOfCards">Copies this entry</Label>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setNewCardData(prev => ({ ...prev, numberOfCards: Math.max(1, (prev.numberOfCards || 1) - 1) }))}
                            className="h-9 w-9 p-0"
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <Input
                            id="lot-numberOfCards"
                            type="number"
                            min="1"
                            value={newCardData.numberOfCards || 1}
                            onChange={(e) => setNewCardData(prev => ({ ...prev, numberOfCards: Math.max(1, parseInt(e.target.value) || 1) }))}
                            className="text-center"
                            readOnly
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setNewCardData(prev => ({ ...prev, numberOfCards: (prev.numberOfCards || 1) + 1 }))}
                            className="h-9 w-9 p-0"
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lot-sport">Sport / TCG</Label>
                      <Input
                        id="lot-sport"
                        value={newCardData.sport}
                        onChange={(e) => setNewCardData(prev => ({ ...prev, sport: e.target.value }))}
                        placeholder="e.g., Basketball, Pokémon"
                      />
                    </div>
                  </TabsContent>
                  <TabsContent value="details" className="mt-4 space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="lot-year">Year</Label>
                        <Input
                          id="lot-year"
                          type="number"
                          value={newCardData.year}
                          onChange={(e) => setNewCardData(prev => ({ ...prev, year: e.target.value }))}
                          placeholder="e.g., 2023"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lot-manufacturer">Manufacturer</Label>
                        <Input
                          id="lot-manufacturer"
                          value={newCardData.manufacturer}
                          onChange={(e) => setNewCardData(prev => ({ ...prev, manufacturer: e.target.value }))}
                          placeholder="e.g., Topps, Panini"
                        />
                      </div>
                      <div className="space-y-2 sm:col-span-2">
                        <Label htmlFor="lot-set">Product / main set</Label>
                        <Input
                          id="lot-set"
                          value={newCardData.set}
                          onChange={(e) => setNewCardData(prev => ({ ...prev, set: e.target.value }))}
                          placeholder="e.g., Topps Chrome"
                        />
                      </div>
                    </div>
                    <CardCatalogFields
                      idPrefix="lot"
                      values={{
                        card_number: newCardData.card_number,
                        subject: newCardData.subject,
                        league: newCardData.league,
                        insert_name: newCardData.insert_name,
                        parallel: newCardData.parallel,
                        numbering: newCardData.numbering,
                      }}
                      onFieldChange={(key, value) => setNewCardData((prev) => ({ ...prev, [key]: value }))}
                    />
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="lot-grade">Grade</Label>
                        <Input
                          id="lot-grade"
                          value={newCardData.grade}
                          onChange={(e) => setNewCardData(prev => ({ ...prev, grade: e.target.value }))}
                          placeholder="e.g., PSA 10"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lot-gradingOrderId">Grading order ID</Label>
                        <Input
                          id="lot-gradingOrderId"
                          value={newCardData.gradingOrderId}
                          onChange={(e) => setNewCardData(prev => ({ ...prev, gradingOrderId: e.target.value }))}
                          placeholder="Optional"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lot-other">Other info</Label>
                      <Input
                        id="lot-other"
                        value={newCardData.other}
                        onChange={(e) => setNewCardData(prev => ({ ...prev, other: e.target.value }))}
                        placeholder="Anything that does not fit the fields above"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lot-notes">Notes</Label>
                      <Textarea
                        id="lot-notes"
                        value={newCardData.notes}
                        onChange={(e) => setNewCardData(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="Private notes"
                        rows={3}
                      />
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            </>
          ) : (
            <>
              <Tabs defaultValue="essentials" className="w-full">
                <TabsList className="grid w-full grid-cols-2 sm:inline-flex">
                  <TabsTrigger value="essentials">Essentials</TabsTrigger>
                  <TabsTrigger value="details">Details</TabsTrigger>
                </TabsList>
                <TabsContent value="essentials" className="mt-4 space-y-4">
                  <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-4">
                    <p className="text-sm font-medium text-foreground">Photos</p>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="front-image" className="text-sm font-normal">Front</Label>
                        <Input
                          id="front-image"
                          type="file"
                          accept="image/*"
                          onChange={(e) => setNewCardData(prev => ({ ...prev, frontImageFile: e.target.files?.[0] || null }))}
                        />
                        {newCardData.frontImageFile && (
                          <p className="text-xs text-muted-foreground">{newCardData.frontImageFile.name}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="back-image" className="text-sm font-normal">Back</Label>
                        <Input
                          id="back-image"
                          type="file"
                          accept="image/*"
                          onChange={(e) => setNewCardData(prev => ({ ...prev, backImageFile: e.target.files?.[0] || null }))}
                        />
                        {newCardData.backImageFile && (
                          <p className="text-xs text-muted-foreground">{newCardData.backImageFile.name}</p>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="other-images" className="text-sm font-normal">Other (up to 3)</Label>
                      <Input
                        id="other-images"
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []).slice(0, MAX_OTHER_IMAGES);
                          setNewCardData(prev => ({ ...prev, otherImageFiles: files }));
                        }}
                      />
                      {newCardData.otherImageFiles.length > 0 && (
                        <p className="text-xs text-muted-foreground">{newCardData.otherImageFiles.length} selected</p>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="card">Card name *</Label>
                    <Input
                      id="card"
                      value={newCardData.card}
                      onChange={(e) => setNewCardData(prev => ({ ...prev, card: e.target.value }))}
                      placeholder="How this card should read in your inventory"
                    />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="purchaseDate">Purchase date *</Label>
                      <Input
                        id="purchaseDate"
                        type="date"
                        value={newCardData.purchaseDate}
                        onChange={(e) => setNewCardData(prev => ({ ...prev, purchaseDate: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="purchasePrice">Purchase price *</Label>
                      <Input
                        id="purchasePrice"
                        type="number"
                        step="0.01"
                        value={newCardData.purchasePrice}
                        onChange={(e) => setNewCardData(prev => ({ ...prev, purchasePrice: e.target.value }))}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="purchasePlatform">Purchase platform</Label>
                      <Input
                        id="purchasePlatform"
                        value={newCardData.purchasePlatform}
                        onChange={(e) => setNewCardData(prev => ({ ...prev, purchasePlatform: e.target.value }))}
                        placeholder="e.g., eBay, COMC"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="boughtFrom">Bought from</Label>
                      <Input
                        id="boughtFrom"
                        value={newCardData.boughtFrom}
                        onChange={(e) => setNewCardData(prev => ({ ...prev, boughtFrom: e.target.value }))}
                        placeholder="Seller or shop"
                      />
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="sport">Sport / TCG</Label>
                      <Input
                        id="sport"
                        value={newCardData.sport}
                        onChange={(e) => setNewCardData(prev => ({ ...prev, sport: e.target.value }))}
                        placeholder="e.g., Basketball, Pokémon"
                      />
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="details" className="mt-4 space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="year">Year</Label>
                      <Input
                        id="year"
                        type="number"
                        value={newCardData.year}
                        onChange={(e) => setNewCardData(prev => ({ ...prev, year: e.target.value }))}
                        placeholder="e.g., 2023"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="manufacturer">Manufacturer</Label>
                      <Input
                        id="manufacturer"
                        value={newCardData.manufacturer}
                        onChange={(e) => setNewCardData(prev => ({ ...prev, manufacturer: e.target.value }))}
                        placeholder="e.g., Topps"
                      />
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="set">Product / main set</Label>
                      <Input
                        id="set"
                        value={newCardData.set}
                        onChange={(e) => setNewCardData(prev => ({ ...prev, set: e.target.value }))}
                        placeholder="e.g., Topps Chrome"
                      />
                    </div>
                  </div>
                  <CardCatalogFields
                    idPrefix="add"
                    values={{
                      card_number: newCardData.card_number,
                      subject: newCardData.subject,
                      league: newCardData.league,
                      insert_name: newCardData.insert_name,
                      parallel: newCardData.parallel,
                      numbering: newCardData.numbering,
                    }}
                    onFieldChange={(key, value) => setNewCardData((prev) => ({ ...prev, [key]: value }))}
                  />
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="grade">Grade</Label>
                      <Input
                        id="grade"
                        value={newCardData.grade}
                        onChange={(e) => setNewCardData(prev => ({ ...prev, grade: e.target.value }))}
                        placeholder="e.g., PSA 10"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="gradingOrderId">Grading order ID</Label>
                      <Input
                        id="gradingOrderId"
                        value={newCardData.gradingOrderId}
                        onChange={(e) => setNewCardData(prev => ({ ...prev, gradingOrderId: e.target.value }))}
                        placeholder="Optional"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="other">Other info</Label>
                    <Input
                      id="other"
                      value={newCardData.other}
                      onChange={(e) => setNewCardData(prev => ({ ...prev, other: e.target.value }))}
                      placeholder="Catch-all for anything else"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                      id="notes"
                      value={newCardData.notes}
                      onChange={(e) => setNewCardData(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Private notes"
                      rows={3}
                    />
                  </div>
                </TabsContent>
              </Tabs>
            </>
          )}
          </div>
          
          <DialogFooter className="shrink-0 border-t border-border px-6 py-4">
            <Button variant="outline" onClick={handleCloseAddCardDialog}>
              Cancel
            </Button>
            {isLotMode ? (
              <>
                <Button variant="outline" onClick={handleAddCardToLot}>
                  Add New Card to Lot
                </Button>
                <Button 
                  variant="gradient" 
                  onClick={handleCompleteLot} 
                  disabled={!newCardData.card || !lotOrderData.purchaseDate || !newCardData.purchasePrice}
                >
                  Complete Lot ({getAllLotCards().length})
                </Button>
              </>
            ) : (
              <Button variant="gradient" onClick={addNewCard}>
                Add Card
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Card Confirmation Dialog */}
      <Dialog open={!!cardToDeleteId} onOpenChange={(open) => !open && setCardToDeleteId(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Card</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this card? This will remove the card and any associated images. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCardToDeleteId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (cardToDeleteId) {
                  handleDeleteTransactionWithImages(cardToDeleteId);
                  setCardToDeleteId(null);
                }
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Cards Confirmation Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onOpenChange={(open) => !isBulkDeleting && setBulkDeleteDialogOpen(open)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Cards</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedItems.size} card(s)? This will remove the cards and any associated images. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteDialogOpen(false)} disabled={isBulkDeleting}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                const selectedIds = Array.from(selectedItems);
                if (selectedIds.length === 0 || !dataService) {
                  setBulkDeleteDialogOpen(false);
                  return;
                }
                setIsBulkDeleting(true);
                try {
                  // Capture current list once so we do a single state update at the end (avoids stale closure overwriting previous deletes)
                  const currentTransactions = transactions;
                  const selectedIdSet = new Set(selectedIds);
                  const dbIdsToDelete: string[] = [];
                  const imagePaths: (string | null | undefined)[] = [];

                  for (const rowId of selectedIds) {
                    const row = currentTransactions.find((t: any) => t.id === rowId) as any;
                    if (!row) continue;
                    const originalIds = row._originalIds?.length
                      ? row._originalIds
                      : [row.id];
                    dbIdsToDelete.push(...originalIds);
                    const rowsToDelete = originalIds
                      .map((id: string) => currentTransactions.find((t: any) => t.id === id))
                      .filter(Boolean) as Transaction[];
                    for (const t of rowsToDelete) {
                      if (t.front_image_path) imagePaths.push(t.front_image_path);
                      if (t.back_image_path) imagePaths.push(t.back_image_path);
                      if (Array.isArray(t.other_image_paths)) imagePaths.push(...t.other_image_paths);
                    }
                  }

                  const pathsToDelete = imagePaths.filter((p): p is string => Boolean(p));
                  if (pathsToDelete.length > 0) {
                    await deleteCardImages(pathsToDelete);
                  }
                  for (const id of dbIdsToDelete) {
                    await dataService.deleteTransaction(id);
                  }

                  const updatedTransactions = currentTransactions.filter(
                    (t: any) => !t.id || !selectedIdSet.has(t.id)
                  );
                  if (onTransactionsUpdate) {
                    onTransactionsUpdate(updatedTransactions);
                  }
                  clearSelection();
                  setBulkDeleteDialogOpen(false);
                  showIndexMessage('success', `Deleted ${selectedIds.length} card(s).`);
                } catch (error) {
                  console.error('Bulk delete error:', error);
                  showIndexMessage('error', 'Failed to delete some cards.');
                } finally {
                  setIsBulkDeleting(false);
                }
              }}
              disabled={isBulkDeleting}
            >
              {isBulkDeleting ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Lot Confirmation Dialog */}
      <Dialog open={lotConfirmationDialogOpen} onOpenChange={setLotConfirmationDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {(() => {
            const allCards = getAllLotCards();
            return (
              <>
                <DialogHeader>
                  <DialogTitle>Confirm Lot - {allCards.length} Card(s)</DialogTitle>
                  <DialogDescription>
                    Review all cards that will be added to your inventory. Click "Confirm & Add All" to complete.
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  {/* Order Summary */}
                  <div className="glass-card rounded-lg p-4 space-y-2">
                    <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Order Summary</h3>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Purchase Date:</span>
                        <p className="font-medium">{lotOrderData.purchaseDate || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Platform:</span>
                        <p className="font-medium">{lotOrderData.purchasePlatform || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Bought From:</span>
                        <p className="font-medium">{lotOrderData.boughtFrom || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="pt-2 border-t">
                      <span className="text-muted-foreground">Total Cards: </span>
                      <span className="font-semibold">{allCards.length}</span>
                      <span className="text-muted-foreground ml-4">Total Value: </span>
                      <span className="font-semibold">
                        ${allCards.reduce((sum, card) => sum + (typeof card.cost === 'number' ? card.cost : parseFloat(card.cost || '0')), 0).toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {/* Cards List */}
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {allCards.map((card, index) => (
                      <div key={index} className="glass-card rounded-lg p-3 border">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="font-medium">{card.card || 'Unnamed Card'}</p>
                            <div className="flex flex-wrap gap-2 mt-2 text-sm text-muted-foreground">
                              {card.sport && <span>{card.sport}</span>}
                              {card.year && <span>• {card.year}</span>}
                              {card.manufacturer && <span>• {card.manufacturer}</span>}
                              {card.set && <span>• {card.set}</span>}
                              {card.grade && <span>• {card.grade}</span>}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">${typeof card.cost === 'number' ? card.cost.toFixed(2) : parseFloat(card.cost || '0').toFixed(2)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setLotConfirmationDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button variant="gradient" onClick={handleConfirmCompleteLot}>
                    Confirm & Add All {allCards.length} Card(s)
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Quantity Selection Dialog */}
      <QuantitySelectionDialog
        open={quantityDialogOpen}
        onClose={() => {
          setQuantityDialogOpen(false);
          // Only reset state if we're not confirming (i.e., user cancelled)
          // Use ref for synchronous check
          if (!isConfirmingQuantityRef.current) {
            setPendingTransaction(null);
            setPendingQuantity(0);
            setPendingAction('sell');
          } else {
            // Reset the flag after handling
            isConfirmingQuantityRef.current = false;
          }
        }}
        onConfirm={(quantity: number) => {
          // Set flag FIRST (synchronously) to prevent onClose from resetting state
          isConfirmingQuantityRef.current = true;
          // Set quantity immediately
          setPendingQuantity(quantity);
          // Then handle the confirmation
          handleQuantityConfirmed(quantity);
        }}
        totalCount={(pendingTransaction as any)?._count || 1}
        actionType={pendingAction}
        cardName={pendingTransaction?.card}
      />

      {/* Bulk Quantity Selection Dialog (for sold) */}
      {currentBulkQuantityTransaction && (
        <QuantitySelectionDialog
          open={bulkQuantityDialogOpen}
          onClose={() => {
            // Only reset if there are no more items in queue
            if (bulkQuantityTransactionsQueue.length <= 1) {
              setBulkQuantityDialogOpen(false);
              setCurrentBulkQuantityTransaction(null);
              setBulkQuantityTransactionsQueue([]);
              setBulkQuantitySelections(new Map());
            }
          }}
          onConfirm={(quantity: number) => {
            handleBulkQuantityConfirmed(quantity);
            // Keep dialog open if there are more items, close if done
            if (bulkQuantityTransactionsQueue.length <= 1) {
              setBulkQuantityDialogOpen(false);
            }
          }}
          totalCount={(currentBulkQuantityTransaction as any)?._count || 1}
          actionType="sell"
          cardName={currentBulkQuantityTransaction?.card}
        />
      )}

      {/* Bulk Grading Quantity Selection Dialog */}
      {currentBulkGradingQuantityTransaction && (
        <QuantitySelectionDialog
          open={bulkGradingQuantityDialogOpen}
          onClose={() => {
            // Only reset if there are no more items in queue
            if (bulkGradingQuantityTransactionsQueue.length <= 1) {
              setBulkGradingQuantityDialogOpen(false);
              setCurrentBulkGradingQuantityTransaction(null);
              setBulkGradingQuantityTransactionsQueue([]);
              setBulkGradingQuantitySelections(new Map());
            }
          }}
          onConfirm={(quantity: number) => {
            handleBulkGradingQuantityConfirmed(quantity);
            // Keep dialog open if there are more items, close if done
            if (bulkGradingQuantityTransactionsQueue.length <= 1) {
              setBulkGradingQuantityDialogOpen(false);
            }
          }}
          totalCount={(currentBulkGradingQuantityTransaction as any)?._count || 1}
          actionType="grading"
          cardName={currentBulkGradingQuantityTransaction?.card}
        />
      )}

      {/* Move to Grading Dialog */}
      <Dialog open={gradingDialogOpen} onOpenChange={setGradingDialogOpen}>
        {gradingDialogStep === 'selectOrder' && (
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Move {pendingQuantity > 0 ? pendingQuantity : 1} Card(s) to Grading</DialogTitle>
              <DialogDescription>
                Select an existing grading order or create a new one
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="select-grading-order">Grading Order *</Label>
                <Select value={selectedGradingOrderId} onValueChange={setSelectedGradingOrderId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a grading order" />
                  </SelectTrigger>
                  <SelectContent>
                    {inProgressGradingOrders.map(order => (
                      <SelectItem key={order.order_id} value={order.order_id}>
                        {order.order_id} - {order.company} ({order.number_of_cards || 0} cards)
                      </SelectItem>
                    ))}
                    <SelectItem value="new">+ Add New Grading Order</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setGradingDialogOpen(false);
                setPendingQuantity(0);
              }}>
                Cancel
              </Button>
              <Button variant="gradient" onClick={handleProceedToNewOrder}>
                Next
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
        
        {gradingDialogStep === 'newOrder' && (
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Grading Order</DialogTitle>
              <DialogDescription>
                Enter details for your grading submission. Fields marked with * are required.
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="new-order-id">Order ID *</Label>
                  <Input
                    id="new-order-id"
                    value={newOrderFromInventoryData.orderId}
                    onChange={(e) => setNewOrderFromInventoryData(prev => ({ ...prev, orderId: e.target.value }))}
                    placeholder="e.g., GR004"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-company">Company *</Label>
                  <Input
                    id="new-company"
                    value={newOrderFromInventoryData.company}
                    onChange={(e) => setNewOrderFromInventoryData(prev => ({ ...prev, company: e.target.value }))}
                    placeholder="e.g., PSA, BGS, SGC"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="new-service-level">Service Level *</Label>
                  <Input
                    id="new-service-level"
                    value={newOrderFromInventoryData.serviceLevel}
                    onChange={(e) => setNewOrderFromInventoryData(prev => ({ ...prev, serviceLevel: e.target.value }))}
                    placeholder="e.g., Economy, Regular, Express"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-submission-date">Submission Date *</Label>
                  <Input
                    id="new-submission-date"
                    type="date"
                    value={newOrderFromInventoryData.submissionDate}
                    onChange={(e) => setNewOrderFromInventoryData(prev => ({ ...prev, submissionDate: e.target.value }))}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-cost">Total Cost</Label>
                <Input
                  id="new-cost"
                  type="number"
                  step="0.01"
                  value={newOrderFromInventoryData.cost}
                  onChange={(e) => setNewOrderFromInventoryData(prev => ({ ...prev, cost: e.target.value }))}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-notes">Notes</Label>
                <Input
                  id="new-notes"
                  value={newOrderFromInventoryData.notes}
                  onChange={(e) => setNewOrderFromInventoryData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Optional notes"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setGradingDialogStep('selectOrder')}>
                Back
              </Button>
              <Button variant="gradient" onClick={handleFinalizeNewOrderFromInventory}>
                Create Order & Move {pendingQuantity > 0 ? pendingQuantity : 1} Card(s)
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
      
      {/* Mark as Sold Dialog */}
      <Dialog open={soldDialogOpen} onOpenChange={(open) => {
        setSoldDialogOpen(open);
        if (!open) {
          // Reset when dialog closes
          setPendingQuantity(0);
          isConfirmingQuantityRef.current = false;
        }
      }}>
        <DialogContent className={pendingQuantity > 1 ? "max-w-2xl max-h-[90vh] overflow-y-auto" : "max-w-md"}>
          <DialogHeader>
            <DialogTitle>Mark as Sold</DialogTitle>
            <DialogDescription>
              {pendingQuantity > 1 
                ? `Record the sale details for ${pendingQuantity} cards. You can set individual prices for each card.`
                : 'Record the sale details for this card.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="sale-date">Sale Date *</Label>
              <Input
                id="sale-date"
                type="date"
                value={soldData.saleDate}
                onChange={(e) => setSoldData(prev => ({ ...prev, saleDate: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="sale-platform">Sale Platform</Label>
              <Input
                id="sale-platform"
                value={soldData.salePlatform}
                onChange={(e) => setSoldData(prev => ({ ...prev, salePlatform: e.target.value }))}
                placeholder="e.g., eBay, COMC, WhatNot"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="sold-to">Sold To</Label>
              <Input
                id="sold-to"
                value={soldData.soldTo}
                onChange={(e) => setSoldData(prev => ({ ...prev, soldTo: e.target.value }))}
                placeholder="Buyer name or identifier"
              />
            </div>

            {pendingQuantity > 1 ? (
              <>
                {/* Individual prices for multiple cards */}
                <div className="space-y-4 border-t pt-4">
                  <Label className="text-base font-semibold">Sale Prices *</Label>
                  {(() => {
                    const originalIds = (soldTransaction as any)?._originalIds || [soldTransaction?.id];
                    const idsToProcess = originalIds.slice(0, pendingQuantity);
                    return idsToProcess.map((id: string, index: number) => {
                      if (!id) return null;
                      const cardName = soldTransaction?.card || `Card ${index + 1}`;
                      return (
                        <div key={id} className="space-y-2">
                          <Label htmlFor={`sale-price-${id}`}>
                            {cardName}
                          </Label>
                          <Input
                            id={`sale-price-${id}`}
                            type="number"
                            step="0.01"
                            value={soldData.individualSalePrices[id] || ''}
                            onChange={(e) => {
                              setSoldData((prev: typeof soldData) => ({
                                ...prev,
                                individualSalePrices: {
                                  ...prev.individualSalePrices,
                                  [id]: e.target.value,
                                },
                              }));
                            }}
                            placeholder="0.00"
                          />
                        </div>
                      );
                    });
                  })()}
                  <div className="pt-2 border-t">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Total Sale Price:</span>
                      <span className="text-sm font-semibold">
                        {formatCurrency(
                          Object.values(soldData.individualSalePrices).reduce((sum, price) => {
                            return sum + (parseFloat(price || '0') || 0);
                          }, 0)
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Individual fees for multiple cards */}
                <div className="space-y-4 border-t pt-4">
                  <Label className="text-base font-semibold">Fees</Label>
                  {(() => {
                    const originalIds = (soldTransaction as any)?._originalIds || [soldTransaction?.id];
                    const idsToProcess = originalIds.slice(0, pendingQuantity);
                    return idsToProcess.map((id: string, index: number) => {
                      if (!id) return null;
                      const cardName = soldTransaction?.card || `Card ${index + 1}`;
                      return (
                        <div key={id} className="space-y-3">
                          <Label className="text-sm font-medium">{cardName}</Label>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-2">
                              <Label htmlFor={`marketplace-fees-${id}`} className="text-xs">Marketplace Fees</Label>
                              <Input
                                id={`marketplace-fees-${id}`}
                                type="number"
                                step="0.01"
                                value={soldData.individualMarketplaceFees[id] || ''}
                                onChange={(e) => {
                                  setSoldData((prev: typeof soldData) => ({
                                    ...prev,
                                    individualMarketplaceFees: {
                                      ...prev.individualMarketplaceFees,
                                      [id]: e.target.value,
                                    },
                                  }));
                                }}
                                placeholder="0.00"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor={`shipping-fees-${id}`} className="text-xs">Shipping Fees</Label>
                              <Input
                                id={`shipping-fees-${id}`}
                                type="number"
                                step="0.01"
                                value={soldData.individualShippingFees[id] || ''}
                                onChange={(e) => {
                                  setSoldData((prev: typeof soldData) => ({
                                    ...prev,
                                    individualShippingFees: {
                                      ...prev.individualShippingFees,
                                      [id]: e.target.value,
                                    },
                                  }));
                                }}
                                placeholder="0.00"
                              />
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </>
            ) : (
              <>
                {/* Single card inputs */}
                <div className="space-y-2">
                  <Label htmlFor="sale-price">Sale Price *</Label>
                  <Input
                    id="sale-price"
                    type="number"
                    step="0.01"
                    value={soldData.salePrice}
                    onChange={(e) => setSoldData(prev => ({ ...prev, salePrice: e.target.value }))}
                    placeholder="0.00"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="marketplace-fees">Marketplace Fees</Label>
                  <Input
                    id="marketplace-fees"
                    type="number"
                    step="0.01"
                    value={soldData.marketplaceFees}
                    onChange={(e) => setSoldData(prev => ({ ...prev, marketplaceFees: e.target.value }))}
                    placeholder="0.00"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="shipping-fees">Shipping Fees</Label>
                  <Input
                    id="shipping-fees"
                    type="number"
                    step="0.01"
                    value={soldData.shippingFees}
                    onChange={(e) => setSoldData(prev => ({ ...prev, shippingFees: e.target.value }))}
                    placeholder="0.00"
                  />
                </div>
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setSoldDialogOpen(false);
              setPendingQuantity(0);
              isConfirmingQuantityRef.current = false;
            }}>
              Cancel
            </Button>
            <Button variant="gradient" onClick={handleSaveMarkAsSold}>
              Mark as Sold
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Move to Grading Dialog */}
      <Dialog open={bulkGradingDialogOpen} onOpenChange={(open) => {
        setBulkGradingDialogOpen(open);
        if (!open) {
          // Reset stored selections when dialog closes
          setStoredBulkGradingQuantitySelections(new Map());
        }
      }}>
        {bulkGradingDialogStep === 'selectOrder' && (
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Move {bulkGradingTotalCardCount || selectedItems.size} Card(s) to Grading</DialogTitle>
              <DialogDescription>
                Select an existing grading order or create a new one
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="bulk-select-grading-order">Grading Order *</Label>
                <Select value={bulkSelectedGradingOrderId} onValueChange={setBulkSelectedGradingOrderId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a grading order" />
                  </SelectTrigger>
                  <SelectContent>
                    {inProgressGradingOrders.map(order => (
                      <SelectItem key={order.order_id} value={order.order_id}>
                        {order.order_id} - {order.company} ({order.number_of_cards || 0} cards)
                      </SelectItem>
                    ))}
                    <SelectItem value="new">+ Add New Grading Order</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkGradingDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="gradient" onClick={handleBulkProceedToNewOrder}>
                Next
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
        
        {bulkGradingDialogStep === 'newOrder' && (
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Grading Order</DialogTitle>
              <DialogDescription>
                Enter details for your grading submission. Fields marked with * are required.
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bulk-new-order-id">Order ID *</Label>
                  <Input
                    id="bulk-new-order-id"
                    value={newOrderFromBulkData.orderId}
                    onChange={(e) => setNewOrderFromBulkData(prev => ({ ...prev, orderId: e.target.value }))}
                    placeholder="e.g., GR004"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bulk-new-company">Company *</Label>
                  <Input
                    id="bulk-new-company"
                    value={newOrderFromBulkData.company}
                    onChange={(e) => setNewOrderFromBulkData(prev => ({ ...prev, company: e.target.value }))}
                    placeholder="e.g., PSA, BGS, SGC"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bulk-new-service-level">Service Level *</Label>
                  <Input
                    id="bulk-new-service-level"
                    value={newOrderFromBulkData.serviceLevel}
                    onChange={(e) => setNewOrderFromBulkData(prev => ({ ...prev, serviceLevel: e.target.value }))}
                    placeholder="e.g., Economy, Regular, Express"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bulk-new-submission-date">Submission Date *</Label>
                  <Input
                    id="bulk-new-submission-date"
                    type="date"
                    value={newOrderFromBulkData.submissionDate}
                    onChange={(e) => setNewOrderFromBulkData(prev => ({ ...prev, submissionDate: e.target.value }))}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="bulk-new-cost">Total Cost</Label>
                <Input
                  id="bulk-new-cost"
                  type="number"
                  step="0.01"
                  value={newOrderFromBulkData.cost}
                  onChange={(e) => setNewOrderFromBulkData(prev => ({ ...prev, cost: e.target.value }))}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bulk-new-notes">Notes</Label>
                <Input
                  id="bulk-new-notes"
                  value={newOrderFromBulkData.notes}
                  onChange={(e) => setNewOrderFromBulkData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Optional notes"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkGradingDialogStep('selectOrder')}>
                Back
              </Button>
              <Button variant="gradient" onClick={handleFinalizeBulkNewOrder}>
                Create Order & Move {(() => {
                  // Calculate total cards after expanding grouped transactions
                  let totalCards = 0;
                  selectedTransactions.forEach(t => {
                    const count = (t as any)._count;
                    if (count && count > 1) {
                      const quantity = bulkGradingQuantitySelections.get(t.id || '') || count;
                      totalCards += quantity;
                    } else {
                      totalCards += 1;
                    }
                  });
                  return totalCards || selectedItems.size;
                })()} Card(s)
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>

      {/* Bulk Mark as Sold Dialog */}
      <Dialog open={bulkSoldDialogOpen} onOpenChange={setBulkSoldDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Mark {bulkSoldData._totalCardCount || selectedItems.size} Card(s) as Sold</DialogTitle>
            <DialogDescription>
              {(() => {
                const cardCount = bulkSoldData._totalCardCount || selectedItems.size;
                if (cardCount === 1) {
                  return 'Enter the sale information for this card.';
                }
                return 'Enter sale information for each card. You can set individual prices or split a total price equally.';
              })()}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Common Fields */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="bulk-sale-date">Sale Date *</Label>
                <Input
                  id="bulk-sale-date"
                  type="date"
                  value={bulkSoldData.saleDate}
                  onChange={(e) => setBulkSoldData(prev => ({ ...prev, saleDate: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="bulk-sale-platform">Sale Platform</Label>
                <Input
                  id="bulk-sale-platform"
                  value={bulkSoldData.salePlatform}
                  onChange={(e) => setBulkSoldData(prev => ({ ...prev, salePlatform: e.target.value }))}
                  placeholder="e.g., eBay, COMC, WhatNot"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bulk-sold-to">Sold To</Label>
                <Input
                  id="bulk-sold-to"
                  value={bulkSoldData.soldTo}
                  onChange={(e) => setBulkSoldData(prev => ({ ...prev, soldTo: e.target.value }))}
                  placeholder="Buyer name or identifier"
                />
              </div>
            </div>

            {/* Sale Prices Section */}
            <div className="space-y-4 border-t pt-4">
              {(() => {
                const cardCount = bulkSoldData._totalCardCount || selectedItems.size;
                const isSingleCard = cardCount === 1;
                
                return (
                  <>
                    {!isSingleCard && (
                      <div className="flex items-center justify-between">
                        <Label className="text-base font-semibold">Sale Prices</Label>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="split-price-equally"
                            checked={bulkSoldData.splitPriceEqually}
                            onCheckedChange={(checked) => {
                              setBulkSoldData(prev => ({ ...prev, splitPriceEqually: checked as boolean }));
                              if (checked) {
                                // Clear individual prices when switching to split mode
                                const clearedPrices: Record<string, string> = {};
                                const allIds = Array.from(bulkSoldData._transactionIdMap?.keys() || []);
                                allIds.forEach(id => {
                                  if (id) clearedPrices[id] = '';
                                });
                                setBulkSoldData(prev => ({ ...prev, individualSalePrices: clearedPrices }));
                              } else {
                                // Clear total price when switching to individual mode
                                setBulkSoldData(prev => ({ ...prev, totalSalePrice: '' }));
                              }
                            }}
                          />
                          <Label htmlFor="split-price-equally" className="text-sm font-normal cursor-pointer">
                            Split total price equally
                          </Label>
                        </div>
                      </div>
                    )}
                    {isSingleCard && (
                      <Label className="text-base font-semibold">Sale Price *</Label>
                    )}
                  </>
                );
              })()}

              {bulkSoldData.splitPriceEqually ? (
                <div className="space-y-2">
                  <Label htmlFor="total-sale-price">Total Sale Price *</Label>
                  <Input
                    id="total-sale-price"
                    type="number"
                    step="0.01"
                    value={bulkSoldData.totalSalePrice}
                    onChange={(e) => {
                      const total = e.target.value;
                      setBulkSoldData(prev => ({ ...prev, totalSalePrice: total }));
                    }}
                    placeholder="0.00"
                  />
                  <p className="text-sm text-muted-foreground">
                    Each card will receive: {bulkSoldData.totalSalePrice && bulkSoldData._totalCardCount
                      ? formatCurrency(parseFloat(bulkSoldData.totalSalePrice) / bulkSoldData._totalCardCount)
                      : formatCurrency(0)}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {(() => {
                    // Get all individual transaction IDs from the map
                    const allIds = Array.from(bulkSoldData._transactionIdMap?.keys() || []);
                    return allIds.map((transactionId, index) => {
                      if (!transactionId) return null;
                      const transaction = bulkSoldData._transactionIdMap?.get(transactionId);
                      const currentPrice = bulkSoldData.individualSalePrices[transactionId] || '';
                      
                      return (
                        <div key={transactionId} className="space-y-2">
                          <Label htmlFor={`sale-price-${transactionId}`}>
                            {transaction?.card || `Card ${index + 1}`}
                          </Label>
                          <Input
                            id={`sale-price-${transactionId}`}
                            type="number"
                            step="0.01"
                            value={currentPrice}
                            onChange={(e) => {
                              setBulkSoldData(prev => ({
                                ...prev,
                                individualSalePrices: {
                                  ...prev.individualSalePrices,
                                  [transactionId]: e.target.value,
                                },
                              }));
                            }}
                            placeholder="0.00"
                          />
                        </div>
                      );
                    });
                  })()}
                  {(() => {
                    const cardCount = bulkSoldData._totalCardCount || selectedItems.size;
                    if (cardCount > 1) {
                      return (
                        <div className="pt-2 border-t">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium">Total Sale Price:</span>
                            <span className="text-sm font-semibold">
                              {formatCurrency(
                                Object.values(bulkSoldData.individualSalePrices).reduce((sum, price) => {
                                  return sum + (parseFloat(price || '0') || 0);
                                }, 0)
                              )}
                            </span>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}
            </div>

            {/* Fees Section */}
            <div className="space-y-4 border-t pt-4">
              {(() => {
                const cardCount = bulkSoldData._totalCardCount || selectedItems.size;
                const isSingleCard = cardCount === 1;
                
                return (
                  <>
                    {!isSingleCard && (
                      <div className="flex items-center justify-between">
                        <Label className="text-base font-semibold">Fees</Label>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="split-fees-equally"
                            checked={bulkSoldData.splitFeesEqually}
                            onCheckedChange={(checked) => {
                              setBulkSoldData(prev => ({ ...prev, splitFeesEqually: checked as boolean }));
                              if (checked) {
                                // Clear individual fees when switching to split mode
                                const clearedMarketplaceFees: Record<string, string> = {};
                                const clearedShippingFees: Record<string, string> = {};
                                const allIds = Array.from(bulkSoldData._transactionIdMap?.keys() || []);
                                allIds.forEach(id => {
                                  if (id) {
                                    clearedMarketplaceFees[id] = '';
                                    clearedShippingFees[id] = '';
                                  }
                                });
                                setBulkSoldData(prev => ({
                                  ...prev,
                                  individualMarketplaceFees: clearedMarketplaceFees,
                                  individualShippingFees: clearedShippingFees,
                                }));
                              } else {
                                // Clear total fees when switching to individual mode
                                setBulkSoldData(prev => ({
                                  ...prev,
                                  totalMarketplaceFees: '',
                                  totalShippingFees: '',
                                }));
                              }
                            }}
                          />
                          <Label htmlFor="split-fees-equally" className="text-sm font-normal cursor-pointer">
                            Split fees equally
                          </Label>
                        </div>
                      </div>
                    )}
                    {isSingleCard && (
                      <Label className="text-base font-semibold">Fees</Label>
                    )}
                  </>
                );
              })()}

              {bulkSoldData.splitFeesEqually ? (
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="total-marketplace-fees">Total Marketplace Fees</Label>
                    <Input
                      id="total-marketplace-fees"
                      type="number"
                      step="0.01"
                      value={bulkSoldData.totalMarketplaceFees}
                      onChange={(e) => setBulkSoldData(prev => ({ ...prev, totalMarketplaceFees: e.target.value }))}
                      placeholder="0.00"
                    />
                    <p className="text-sm text-muted-foreground">
                      Each card: {bulkSoldData.totalMarketplaceFees && selectedTransactions.length > 0
                        ? formatCurrency(parseFloat(bulkSoldData.totalMarketplaceFees) / selectedTransactions.length)
                        : formatCurrency(0)}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="total-shipping-fees">Total Shipping Fees</Label>
                    <Input
                      id="total-shipping-fees"
                      type="number"
                      step="0.01"
                      value={bulkSoldData.totalShippingFees}
                      onChange={(e) => setBulkSoldData(prev => ({ ...prev, totalShippingFees: e.target.value }))}
                      placeholder="0.00"
                    />
                    <p className="text-sm text-muted-foreground">
                      Each card: {bulkSoldData.totalShippingFees && selectedTransactions.length > 0
                        ? formatCurrency(parseFloat(bulkSoldData.totalShippingFees) / selectedTransactions.length)
                        : formatCurrency(0)}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {(() => {
                    // Get all individual transaction IDs from the map
                    const allIds = Array.from(bulkSoldData._transactionIdMap?.keys() || []);
                    return allIds.map((transactionId: string, index: number) => {
                      if (!transactionId) return null;
                      const transaction = bulkSoldData._transactionIdMap?.get(transactionId);
                      return (
                        <div key={transactionId} className="space-y-2 border rounded-lg p-3">
                          <Label className="text-sm font-medium">{transaction?.card || `Card ${index + 1}`}</Label>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label htmlFor={`marketplace-fee-${transactionId}`} className="text-xs">
                                Marketplace Fees
                              </Label>
                              <Input
                                id={`marketplace-fee-${transactionId}`}
                                type="number"
                                step="0.01"
                                value={bulkSoldData.individualMarketplaceFees[transactionId] || ''}
                                onChange={(e) => {
                                  setBulkSoldData(prev => ({
                                    ...prev,
                                    individualMarketplaceFees: {
                                      ...prev.individualMarketplaceFees,
                                      [transactionId]: e.target.value,
                                    },
                                  }));
                                }}
                                placeholder="0.00"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label htmlFor={`shipping-fee-${transactionId}`} className="text-xs">
                                Shipping Fees
                              </Label>
                              <Input
                                id={`shipping-fee-${transactionId}`}
                                type="number"
                                step="0.01"
                                value={bulkSoldData.individualShippingFees[transactionId] || ''}
                                onChange={(e) => {
                                  setBulkSoldData(prev => ({
                                    ...prev,
                                    individualShippingFees: {
                                      ...prev.individualShippingFees,
                                      [transactionId]: e.target.value,
                                    },
                                  }));
                                }}
                                placeholder="0.00"
                              />
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                  {(() => {
                    const cardCount = bulkSoldData._totalCardCount || selectedItems.size;
                    if (cardCount > 1) {
                      return (
                        <div className="pt-2 border-t">
                          <div className="flex justify-between items-center text-sm">
                            <span className="font-medium">Total Marketplace Fees:</span>
                            <span className="font-semibold">
                              {formatCurrency(
                                Object.values(bulkSoldData.individualMarketplaceFees).reduce((sum, fee) => {
                                  return sum + (parseFloat(fee || '0') || 0);
                                }, 0)
                              )}
                            </span>
                          </div>
                          <div className="flex justify-between items-center text-sm mt-1">
                            <span className="font-medium">Total Shipping Fees:</span>
                            <span className="font-semibold">
                              {formatCurrency(
                                Object.values(bulkSoldData.individualShippingFees).reduce((sum, fee) => {
                                  return sum + (parseFloat(fee || '0') || 0);
                                }, 0)
                              )}
                            </span>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkSoldDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="gradient" onClick={handleSaveBulkMarkAsSold}>
              Mark {bulkSoldData._totalCardCount || selectedItems.size} Card(s) as Sold
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Column Picker Dialog */}
      <ColumnPicker
        open={columnPickerOpen}
        onClose={() => setColumnPickerOpen(false)}
        title={`${activeColumnPicker === 'inventory' ? 'Inventory' : 'Sold Cards'} - Column Preferences`}
        columns={activeColumnPicker === 'inventory' ? inventoryColumns : soldColumns}
        visibleColumns={activeColumnPicker === 'inventory' ? inventoryVisibleColumns : soldVisibleColumns}
        columnOrder={activeColumnPicker === 'inventory' ? inventoryColumnOrder : soldColumnOrder}
        onColumnVisibilityChange={handleColumnVisibilityChange}
      />

      {/* Quick Filters Dialog */}
      <QuickFilters
        open={quickFiltersOpen}
        onClose={() => setQuickFiltersOpen(false)}
        onApplyFilters={handleApplyFilters}
        transactions={transactions}
      />
    </div>
  );
};

export default Index;